<template>
  <div class="montador builder-wrap">
    <!-- Sidebar -->
    <aside class="sidebar" @keydown.stop>
      <h2 class="title">Montador de Pista (Mapa Técnico)</h2>

      <SidebarSection idKey="status" title="Status & Ações">
        <SidebarSelectedInfo />
        <SidebarMeasure />
      </SidebarSection>

      <SidebarSection idKey="arena" title="Arena & Escala">
        <SidebarScale />
        <SidebarArena :open-params="openParamsModal" @open-params="openParamsModal" />
      </SidebarSection>

      <SidebarSection idKey="obstaculo" title="Obstáculo">
        <SidebarObstacles @add-obstacle="addObstacle" />
        <SidebarCombos @create-combo="onCreateCombo" />
        <SidebarOverrides />
      </SidebarSection>

      <SidebarSection idKey="percurso" title="Percurso & Relatório">
        <SidebarCourse @clear-course="clearCourse" />
        <SidebarSpeed
          v-model:preset="speedPreset"
          v-model:custom="speedCustom"
          :course-meters="courseMeters"
          :ta-sec="taSec"
          :tl-sec="tlSec"
          :opt-sec="optSec"
        />
        <SidebarIO
          @import-json="onImport"
          @export-json="exportJSON"
          @generate-pdf="gerarRelatorioMontagemPDF"
          @center-all="centerAll"
          @clear-all="clearAll"
        />
      </SidebarSection>
    </aside>

    <!-- Arena -->
    <div
      class="arena"
      :style="arenaStyle"
      @pointerdown="onArenaDown"
      @wheel.passive="onWheel"
      ref="arenaRef"
    >
      <div class="content">
        <!-- ✅ Um único container com o transform da câmera -->
        <div class="view" :style="viewStyle">
          <!-- Grade de fundo -->
          <div class="grid" :style="gridStyle"></div>

          <!-- Arena + réguas/handles -->
          <ArenaOutline
            :arenaGeom="arenaGeom"
            :arenaPx="arenaPx"
            :scale="state.scale"
            :marksX="arenaRulers.marksX"
            :marksY="arenaRulers.marksY"
            :rulersInsetPx="params.visual.rulersInsetPx"
            :showHandles="state.arenaMode !== 'rect' && state.free.showHandles"
            :freeVerts="state.free.verts"
            :showRulers="!!params.visual.showRulers"
            @vertex-down="onVertexDown"
          />

          <!-- Obstáculos -->
          <div
            :style="{
              position: 'absolute',
              inset: 0,
              pointerEvents: overlaysBlocked ? 'none' : 'auto'
            }"
          >
            <ObstaclesLayer
              :obstacles="state.obstacles"
              :selectedId="state.selectedId"
              :scale="state.scale"
              :viewScale="state.view?.scale || 1"
              :getObsSpecs="getObsSpecs"
              :courseLabel="courseLabel"
              @obstacle-down="onObstacleDown"
              @obstacle-click="onObstacleClick"
              @rotate-down="onRotateDown"
            />
          </div>

          <!-- Percurso -->
          <svg
            v-if="state.tools.course.length >= 2"
            class="course-lines"
            :width="arenaPx.w"
            :height="arenaPx.h"
            :style="{ pointerEvents: overlaysBlocked ? 'none' : 'auto' }"
          >
            <template v-for="seg in courseSegments" :key="seg.key">
              <line
                :x1="seg.p0.x * state.scale" :y1="seg.p0.y * state.scale"
                :x2="seg.r1.x * state.scale" :y2="seg.r1.y * state.scale"
                stroke="#2563eb" stroke-width="2" stroke-dasharray="6 4" fill="none"
              />
              <path
                :d="`M ${seg.r1.x * state.scale} ${seg.r1.y * state.scale}
                    C ${seg.c1.x * state.scale} ${seg.c1.y * state.scale},
                      ${seg.c2.x * state.scale} ${seg.c2.y * state.scale},
                      ${seg.r2.x * state.scale} ${seg.r2.y * state.scale}`"
                stroke="#2563eb" stroke-width="2" stroke-dasharray="6 4" fill="none"
              />
              <line
                :x1="seg.r2.x * state.scale" :y1="seg.r2.y * state.scale"
                :x2="seg.p3.x * state.scale" :y2="seg.p3.y * state.scale"
                stroke="#2563eb" stroke-width="2" stroke-dasharray="6 4" fill="none"
              />
              <template v-if="params.visual.showDistanceLabels">
                <text
                  :x="seg.label.x * state.scale" :y="seg.label.y * state.scale"
                  text-anchor="middle" font-size="12" font-weight="bold"
                  fill="#111" stroke="#fff" stroke-width="3" paint-order="stroke"
                >
                  {{ seg.dist.toFixed(params.visual.decimals) }} m
                </text>
              </template>
            </template>
          </svg>

          <!-- Medição dinâmica -->
          <svg
            v-if="state.tools.measureActive && measureLine"
            class="measure"
            :width="arenaPx.w"
            :height="arenaPx.h"
            :style="{ pointerEvents: 'none' }"
          >
            <!-- linha -->
            <line
              :x1="measureLine.x1 * state.scale"
              :y1="measureLine.y1 * state.scale"
              :x2="measureLine.x2 * state.scale"
              :y2="measureLine.y2 * state.scale"
              class="measure-line"
              :class="{ preview: !!state.tools.measureHover }"
            />

            <!-- ponto inicial -->
            <circle
              :cx="measureLine.x1 * state.scale"
              :cy="measureLine.y1 * state.scale"
              r="3.5"
              class="measure-point start"
            />
            <!-- ponto final (ou hover) -->
            <circle
              :cx="measureLine.x2 * state.scale"
              :cy="measureLine.y2 * state.scale"
              r="3.5"
              class="measure-point end"
              :class="{ preview: !!state.tools.measureHover }"
            />
          </svg>

          <!-- Rótulo da medição -->
          <div
            v-if="state.tools.measureActive && measureP0 && measureP1"
            class="measure-label"
            :style="[{ pointerEvents: 'none' }, measureLabelStyle]"
          >
            {{ measureDistance.toFixed(params.visual.decimals) }} m
          </div>
        </div>

        <!-- Badges (fora do transform, mas dentro da .arena) -->
        <div class="mode-badge" v-if="state.tools.measureActive">Medição ativa</div>
        <div class="mode-badge" v-if="state.tools.pathActive" style="top: 44px">
          Percurso ativo (clique nos obstáculos)
        </div>
      </div>
    </div>
  </div>

<ParamsModal
  :open="paramsModal.open"
  :params="params"
  @close="paramsModal.open = false"
  @save="handleParamsSave"
/>

</template>

<script setup>
import { saveParamsToLocal, loadParamsFromLocal } from '@/components/Ajuda/Montador/lib/defaultParametros'
import { reactive, ref, computed, unref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { parseNumberList, formatListSemicolon } from '@/components/Ajuda/Montador/lib/numbers'
import { clampToPolygon, clamp } from '@/components/Ajuda/Montador/lib/geom'
import { cubicPointAt, cubicTangentAt, radiusAtStart, radiusAtEnd } from '@/components/Ajuda/Montador/lib/bezier'
import { useHistory, registerHistoryHotkeys } from '@/components/Ajuda/Montador/composables/useHistory'
// import ObstacleShape from '@/components/Ajuda/Montador/components/Canvas/ObstacleShape.vue'
// import { drawObstacleIcon } from '@/components/Ajuda/Montador/lib/drawObstacleIcon'
import ArenaOutline from '@/components/Ajuda/Montador/components/Canvas/ArenaOutline.vue'
import ObstaclesLayer from '@/components/Ajuda/Montador/components/Canvas/ObstaclesLayer.vue'
import { state, params, selected, getObsSpecs,actions, getComboFaces, setComboFaces } from '@/components/Ajuda/Montador/composables/useBuilderState'
import SidebarScale from '@/components/Ajuda/Montador/sidebar/SidebarScale.vue'
import SidebarArena from '@/components/Ajuda/Montador/sidebar/SidebarArena.vue'
import SidebarObstacles from '@/components/Ajuda/Montador/sidebar/SidebarObstacles.vue'
import SidebarCourse from '@/components/Ajuda/Montador/sidebar/SidebarCourse.vue'
import SidebarCombos from '@/components/Ajuda/Montador/sidebar/SidebarCombos.vue'
import SidebarMeasure from '@/components/Ajuda/Montador/sidebar/SidebarMeasure.vue'
import SidebarIO      from '@/components/Ajuda/Montador/sidebar/SidebarIO.vue'
import SidebarOverrides from '@/components/Ajuda/Montador/sidebar/SidebarOverrides.vue'
import SidebarSelectedInfo from '@/components/Ajuda/Montador/sidebar/SidebarSelectedInfo.vue'
import SidebarSection from '@/components/Ajuda/Montador/sidebar/SidebarSection.vue'
import SidebarSpeed  from '@/components/Ajuda/Montador/sidebar/SidebarSpeed.vue'
import ParamsModal from '@/components/Ajuda/Montador/components/Modals/ParamsModal.vue'


let idSeq = 1
const arenaRef = ref(null)

/* ===================== Parâmetros Globais (migrados p/ composable) ===================== */
// Aplique seus defaults "legados" no `params` IMPORTADO do composable:
const LEGACY_PARAMS = {
  version: 1,
  arena: { w: 60, h: 30, snapGrid: 0.5, scale: 16, freeShowHandles: true, freeConfine: false },
  obstacles: {
    vertical:  { wm: 3.75, hm: 1.2, dir: 1, barThickness: 0.10 },
    oxer:      { wm: 3.75, hm: 1.2, dir: 1, thicknesses: [0.10, 0.10],       gaps: [0.60],       barHeights: [0.60, 0.80] },
    tripleBar: { wm: 3.75, hm: 1.6, dir: 1, thicknesses: [0.10, 0.10, 0.10], gaps: [0.50, 0.60], barHeights: [0.60, 0.80, 1.00] },
    gate:      { wm: 3.75, hm: 1.2, dir: 1, barThickness: 0.15 },
    plank:     { wm: 3.75, hm: 1.2, dir: 1, barThickness: 0.15 },
    wall:      { wm: 3.75, hm: 1.2, dir: 1, barThickness: 0.20 },
    start:     { wm: 4.75, hm: 2.0, dir: 1, barThickness: 0.10, minOutStraight: 6 },
    finish:    { wm: 4.75, hm: 2.0, dir: 1, barThickness: 0.10, minInStraight: 6 },
  },
  combos: { defaultCentro: 7.5, centerOnCreate: true },
  path: { pathTension: 0.35, curveSmoothness: 0.5, exitStrides: 2.0, entryStrides: 1.3, rMin: 6.0, clearance: 1.5 },
  visual: { showDistanceLabels: true, decimals: 2 }
}

applyLegacyDefaultsOnce(LEGACY_PARAMS)

function applyLegacyDefaultsOnce(p) {
  // evita rodar mais de uma vez
  if (params.__legacyApplied) return
  params.__legacyApplied = true

  // merge “apenas se estiver vazio/ausente”
  const fill = (t, s) => {
    if (!s || typeof s !== 'object') return
    for (const k of Object.keys(s)) {
      const sv = s[k], tv = t[k]
      if (tv == null) { t[k] = structuredClone(sv); continue }
      if (Array.isArray(sv))  { if (!Array.isArray(tv) || tv.length === 0) t[k] = structuredClone(sv); continue }
      if (typeof sv === 'object') { if (typeof tv !== 'object' || tv == null) { t[k] = structuredClone(sv) } else { fill(tv, sv) } }
    }
  }

  // aplica defaults sem sobrescrever existentes
  fill(params, p)

  // normalizações de obstáculos (sinônimos + alturas)
  params.obstacles = params.obstacles || {}
  // sinônimos
  if (params.obstacles.muro && !params.obstacles.wall)
    params.obstacles.wall = structuredClone(params.obstacles.muro)
  if (params.obstacles.triple && !params.obstacles.tripleBar)
    params.obstacles.tripleBar = structuredClone(params.obstacles.triple)

  // migrar barHeights/alturas → heights e limpar aliases
  for (const [k, o0] of Object.entries(params.obstacles)) {
    const o = o0 || {}
    if (Array.isArray(o.barHeights) && !Array.isArray(o.heights)) o.heights = o.barHeights.slice()
    if (Array.isArray(o?.bars?.heights) && !Array.isArray(o.heights)) o.heights = o.bars.heights.slice()
    delete o.barHeights
    if (o.bars) delete o.bars.heights
    delete o.alturas
    delete o.heightsStr
    delete o.alturasStr
    params.obstacles[k] = o
  }
}


/* === Bootstrap do shape esperado em `state` (composable) === */
if (state.arenaWm == null) state.arenaWm = params.arena.w
if (state.arenaHm == null) state.arenaHm = params.arena.h
if (state.scale   == null) state.scale   = params.arena.scale
if (state.arenaMode == null) state.arenaMode = 'rect'
if (state.snap == null) state.snap = true
if (state.drag == null) state.drag = { active:false, mode:null, id:null, start:null, shift:false }
if (state.nextLabel == null) state.nextLabel = 1
if (state.moveStep == null) state.moveStep = 0.1
// view extra (pan/zoom)
state.view = {
  scale: state.view?.scale ?? 1,
  x:     state.view?.x     ?? 0,
  y:     state.view?.y     ?? 0,
  panning: false,
  panStart: null,
}
// tools esperados neste arquivo
if (!state.tools) state.tools = {}
Object.assign(state.tools, {
  measureActive:  state.tools.measureActive  ?? false,
  measurePts:     state.tools.measurePts     ?? [],
  pathActive:     state.tools.pathActive     ?? false,
  course:         state.tools.course         ?? [],
  clickMode:      state.tools.clickMode      ?? 'montar',
  pathTension:    state.tools.pathTension    ?? params.path.pathTension,
  curveSmoothness:state.tools.curveSmoothness?? params.path.curveSmoothness,
  exitStrides:    state.tools.exitStrides    ?? params.path.exitStrides,
  entryStrides:   state.tools.entryStrides   ?? params.path.entryStrides,
  rMin:           state.tools.rMin           ?? params.path.rMin,
})
// arena livre (4 vértices)
if (!state.free) state.free = { verts: [], showHandles: params.arena.freeShowHandles, confine: params.arena.freeConfine }
if (!state.free.verts || state.free.verts.length !== 4) {
  state.free.verts = [
    { x: 0, y: 0 },
    { x: params.arena.w, y: 0 },
    { x: params.arena.w, y: params.arena.h },
    { x: 0, y: params.arena.h }
  ]
}



// [RULER-DEFAULTS] — régua nas bordas
if (!params.visual) params.visual = {};
if (typeof params.visual.showRulers !== 'boolean') params.visual.showRulers = true; // liga por padrão
if (typeof params.visual.rulerStep !== 'number') params.visual.rulerStep = 10;      // 10 m
if (typeof params.visual.rulersInsetPx !== 'number') params.visual.rulersInsetPx = 12; // tamanho do "tique" (px)

if (params.visual == null) params.visual = {};

function specFor(o) {
  if (!o) return {}
  const base  = (params?.obstacles?.[o.type]) || {}
  const local = o.local || {}

  const thicknesses =
    (Array.isArray(local.thicknesses) && local.thicknesses.length) ? local.thicknesses
    : (Array.isArray(base.thicknesses)  && base.thicknesses.length)  ? base.thicknesses
    : (typeof base.barThickness === 'number' ? [base.barThickness] : [])

  const gaps =
    (Array.isArray(local.gaps) && local.gaps.length) ? local.gaps
    : (Array.isArray(base.gaps) && base.gaps.length) ? base.gaps
    : []

  return {
    type: o.type,
    widthM:  o.widthM ?? o.wm ?? base.widthM ?? 3,
    thicknesses,
    gaps,
    spreadM: local.spreadM ?? base.spreadM ?? 0,
    postWidthM: base.postWidthM ?? 0.08,
    color: base.color ?? '#3a3a3a',
  }
}

/* ===================== Modal de Parâmetros ===================== */
const paramsModal = reactive({
  open: false,
  tab: 'arena',
  typeTab: 'vertical',
  applySelected: false,
  applyType: false,
  applyAll: false,
  applyArenaNow: false
})

const paramsEdit = reactive(structuredClone(params))

// strings para listas (editáveis no modal)
paramsEdit.oxerThicksStr     = formatListSemicolon(params.obstacles.oxer.thicknesses, 2);
paramsEdit.oxerGapsStr       = formatListSemicolon(params.obstacles.oxer.gaps, 2);
paramsEdit.oxerHeightsStr    = formatListSemicolon((params.obstacles.oxer.barHeights || []), 2);
paramsEdit.tripleThicksStr   = formatListSemicolon(params.obstacles.tripleBar.thicknesses, 2);
paramsEdit.tripleGapsStr     = formatListSemicolon(params.obstacles.tripleBar.gaps, 2);
paramsEdit.tripleHeightsStr  = formatListSemicolon((params.obstacles.tripleBar.barHeights || []), 2);

function openParamsModal() {
  // 1) Abre primeiro — mesmo que algo falhe depois, você verá o modal
  if (!paramsModal.tab) paramsModal.tab = 'arena'
  paramsModal.open = true

  // 2) Preenche os campos numa próxima batida de render, com try/catch
  nextTick(() => {
    try {
      Object.assign(paramsEdit, structuredClone(params))

      // Strings das listas (com fallback seguro)
      paramsEdit.oxerThicksStr     = formatListSemicolon(params.obstacles?.oxer?.thicknesses || [], 2);
      paramsEdit.oxerGapsStr       = formatListSemicolon(params.obstacles?.oxer?.gaps || [], 2);
      paramsEdit.oxerHeightsStr    = formatListSemicolon(
        (params.obstacles?.oxer?.barHeights || params.obstacles?.oxer?.heights || []), 2
      );

      paramsEdit.tripleThicksStr   = formatListSemicolon(params.obstacles?.tripleBar?.thicknesses || [], 2);
      paramsEdit.tripleGapsStr     = formatListSemicolon(params.obstacles?.tripleBar?.gaps || [], 2);
      paramsEdit.tripleHeightsStr  = formatListSemicolon(
        (params.obstacles?.tripleBar?.barHeights || params.obstacles?.tripleBar?.heights || []), 2
      );
      // Garantia de objeto visual + default do toggle (sem ??= / ??)
      if (paramsEdit.visual == null) paramsEdit.visual = {};
      if (typeof paramsEdit.visual.preferGlobalRMin !== 'boolean') {
        paramsEdit.visual.preferGlobalRMin =
          (params.visual && typeof params.visual.preferGlobalRMin === 'boolean')
            ? params.visual.preferGlobalRMin
            : true;
      }
    } catch (err) {
      console.error('[ParamsModal] erro ao preparar paramsEdit:', err)
    }
  })
}

function onCreateCombo({ types, distances }) {
  // usa a função existente do pai
  addCombo(types, distances)
}

function addCombo(types, distances) {
  historyPush('addCombo')
  const count = types.length

  const existingGroups = state.obstacles.filter(o => o.comboGroup).map(o => o.comboGroup)
  const nextGroup = existingGroups.length ? Math.max(...existingGroups) + 1 : 1

  const letters = ['a', 'b', 'c']
  const baseX = state.arenaWm / 2
  const baseY = state.arenaHm / 2

  const baseRot = 90
  const moveAngle = (baseRot - 90) * Math.PI / 180

  // 1) Soma total (centro-a-centro), usando offset de faces
  let totalCentroCentro = 0
  for (let i = 0; i < distances.length; i++) {
    const tA = types[i]
    const tB = types[i + 1]
    const dummyA = { id:-1, type: tA, xm:0, ym:0, rot: baseRot, dir: (getTypeParams(tA)?.dir ?? 1), local:{} }
    const dummyB = { id:-1, type: tB, xm:0, ym:0, rot: baseRot, dir: (getTypeParams(tB)?.dir ?? 1), local:{} }
    totalCentroCentro += distances[i] + measureOffsetObs(dummyA) + measureOffsetObs(dummyB)
  }

  // 2) Centraliza o combo e só então começa a avançar
  let currentOffset = -totalCentroCentro / 2

  // 3) Criação das partes
  for (let i = 0; i < count; i++) {
    const tipo = types[i]
    const dx = currentOffset * Math.cos(moveAngle)
    const dy = currentOffset * Math.sin(moveAngle)

    const obs = {
      id: idSeq++,
      type: tipo,
      comboGroup: nextGroup,
      comboPart: letters[i],
      comboOrder: i,                    // < novo
      comboDistances: distances.slice(),// < novo (ex.: [7.5] ou [7.5,7.5])
      xm: baseX + dx,
      ym: baseY + dy,
      rot: baseRot,
      z: Date.now(),
      label: String(state.nextLabel++),
      dir: getTypeParams(tipo).dir,
      local: {}
    }

    state.obstacles.push(obs)

    // Avança offset para o próximo vão (centro-a-centro)
    if (i < distances.length) {
      const tA = types[i]
      const tB = types[i + 1]
      const dummyA = { id:-1, type: tA, xm:0, ym:0, rot: baseRot, dir: (getTypeParams(tA)?.dir ?? 1), local:{} }
      const dummyB = { id:-1, type: tB, xm:0, ym:0, rot: baseRot, dir: (getTypeParams(tB)?.dir ?? 1), local:{} }
      currentOffset += distances[i] + measureOffsetObs(dummyA) + measureOffsetObs(dummyB)
    }
  }

  state.selectedId = state.obstacles[state.obstacles.length - count].id
}

// SUBSTITUIR: saveParams com normalização antes da validação
function handleParamsSave(payload) {
  // payload: { mode, params: newParams, flags, tab, typeTab }
  const { mode = 'save', params: next, flags = {}, typeTab } = payload || {}

  // 1) Commit nos reativos 'params'
  mergeParams(next)

  // --- APLICAR DIMENSÕES À ARENA ATUAL (quando marcado no modal) ---
  const applyNow = !!(next?.arena?.applyNow)
  if (applyNow) {
    const w = Number(next?.arena?.w ?? params.arena?.w)
    const h = Number(next?.arena?.h ?? params.arena?.h)
    if (Number.isFinite(w) && Number.isFinite(h)) {
      // atualiza tamanho atual da arena em METROS
      state.arenaWm = w
      state.arenaHm = h

      // se estiver em modo livre/4 lados, refaz os vértices do retângulo base
      if (state.arenaMode !== 'rect') {
        state.free = state.free || {}
        state.free.verts = [
          { x: 0, y: 0 },
          { x: w, y: 0 },
          { x: w, y: h },
          { x: 0, y: h },
        ]
      }
    }

    // reseta a flag no params para não ficar “grudada” no próximo save
    if (next.arena) next.arena.applyNow = false
    if (params.arena) params.arena.applyNow = false
  }

  const prevGlobals = {
    pathTension:    state.tools.pathTension,
    curveSmoothness:state.tools.curveSmoothness,
    exitStrides:    state.tools.exitStrides,
    entryStrides:   state.tools.entryStrides,
    rMin:           state.tools.rMin,
  }

  // 2) Sincroniza knobs globais no estado atual
  state.scale                = Number(params.arena.scale) || state.scale
  if (flags.applyArenaNow) {
    state.arenaWm           = Number(params.arena.w) || state.arenaWm
    state.arenaHm           = Number(params.arena.h) || state.arenaHm
  if (state.arenaMode !== 'rect') {
    state.free.verts = [
      { x: 0, y: 0 },
      { x: state.arenaWm, y: 0 },
      { x: state.arenaWm, y: state.arenaHm },
      { x: 0, y: state.arenaHm },
    ]
      state.free.showHandles = !!params.arena.freeShowHandles
      state.free.confine     = !!params.arena.freeConfine
    }
  }
  state.tools.pathTension     = Number(params.path.pathTension)
  state.tools.curveSmoothness = Number(params.path.curveSmoothness)
  state.tools.exitStrides     = Number(params.path.exitStrides)
  state.tools.entryStrides    = Number(params.path.entryStrides)
  state.tools.rMin            = Number(params.path.rMin)


    // >>> migração: remove valores locais iguais ao snapshot anterior (voltam a herdar)
  state.tools.course = state.tools.course.map(seg => {
    if (!seg || typeof seg !== 'object') return seg
    const out = { id: seg.id }
    if (seg.pathTension    != null && seg.pathTension    !== prevGlobals.pathTension)     out.pathTension    = seg.pathTension
    if (seg.curveSmoothness!= null && seg.curveSmoothness!== prevGlobals.curveSmoothness) out.curveSmoothness= seg.curveSmoothness
    if (seg.exitStrides    != null && seg.exitStrides    !== prevGlobals.exitStrides)     out.exitStrides    = seg.exitStrides
    if (seg.entryStrides   != null && seg.entryStrides   !== prevGlobals.entryStrides)    out.entryStrides   = seg.entryStrides
    if (seg.rMin           != null && seg.rMin           !== prevGlobals.rMin)            out.rMin           = seg.rMin
    return out
  })

  // 3) Aplicações por escopo (igual ao comportamento antigo)
  const modeApplySelected = mode === 'applySelected' || !!flags.applySelected
  const modeApplyType     = mode === 'applyType'     || !!flags.applyType
  const modeApplyAll      = mode === 'applyAll'      || !!flags.applyAll

  if (modeApplySelected && selected.value) {
    applyGlobalsToObstacle(selected.value)
  }
  if (modeApplyType && typeTab) {
    const t = normalizeType(typeTab)
    state.obstacles.filter(o => normalizeType(o.type) === t)
      .forEach(o => applyGlobalsToObstacle(o))
  }
  if (modeApplyAll) {
    state.obstacles.forEach(o => applyGlobalsToObstacle(o))
  }

  try { saveParamsToLocal(params) } catch (e) { console.warn('[Montador] save params failed:', e) }
}

function normalizeType(t) {
  const s = String(t || '').toLowerCase()
  if (s === 'triple' || s === 'triplebar') return 'tripleBar'
  return s
}

function mergeParams(next) {
  if (!next || typeof next !== 'object') return

  // Garante objetos-base
  params.arena     = { ...(params.arena || {}),     ...(next.arena || {}) }
  params.combos    = { ...(params.combos || {}),    ...(next.combos || {}) }
  params.path      = { ...(params.path || {}),      ...(next.path || {}) }
  params.visual    = { ...(params.visual || {}),    ...(next.visual || {}) }
  params.obstacles = { ...(params.obstacles || {}) }

  const srcObs = next.obstacles || {}
  const allTypes = [
    'vertical','oxer','tripleBar','gate','plank','wall','start','finish'
  ]
  for (const t of allTypes) {
    if (!params.obstacles[t]) params.obstacles[t] = {}
    if (srcObs[t]) {
      // copia profunda simples (sem perder reatividade do objeto raiz)
      Object.assign(params.obstacles[t], srcObs[t])
      // sanidade para listas (garante arrays)
      if (t === 'oxer' || t === 'tripleBar') {
        const o = params.obstacles[t]
        o.thicknesses = Array.isArray(o.thicknesses) ? o.thicknesses : []
        o.gaps        = Array.isArray(o.gaps)        ? o.gaps        : []
        // alturas podem vir como heights ou barHeights — mantenha barHeights se existir
        if (!Array.isArray(o.barHeights)) o.barHeights = Array.isArray(o.heights) ? [...o.heights] : []
      }
    }
  }
}

// Copia padrões globais para o obstáculo (como overrides locais)
function applyGlobalsToObstacle(o){
  const p = params.obstacles[o.type]
  o.local = o.local || {}

  // Frente ANTES
  const specBefore = getObsSpecs(o)
  const thB = (specBefore.thicknesses?.length ? specBefore.thicknesses : [specBefore.barThickness ?? 0.10])
  const gpB = specBefore.gaps || []
  const sum = (a)=> (Array.isArray(a)?a.reduce((x,y)=>x+(+y||0),0):0)
  const totB=sum(thB)+sum(gpB)
  const lastB = thB[thB.length-1]||0
  const ang=(o.rot-90)*Math.PI/180
  const sign=(specBefore.dir===-1?-1:1)
  const nx=Math.cos(ang)*sign, ny=Math.sin(ang)*sign
  const offB=totB/2-lastB/2
  const fxB=o.xm+nx*offB, fyB=o.ym+ny*offB

  // aplica presets
  o.local.wm=p.wm; o.local.hm=p.hm; o.local.dir=p.dir
  if (['vertical','gate','plank','wall','start','finish'].includes(o.type)) {
    o.local.barThickness=p.barThickness
    if (Array.isArray(p.heights) && p.heights.length) o.local.heights=[Number(p.heights[0])]; else delete o.local.heights
  }
  if (['oxer','tripleBar','triple'].includes(o.type)) {
    o.local.thicknesses=[...(p.thicknesses||[])]; o.local.gaps=[...(p.gaps||[])]
    o.local.heights=Array.isArray(p.heights)?[...p.heights]:(Array.isArray(p.barHeights)?[...p.barHeights]:[])
  }

  // Frente DEPOIS
  const specAfter=getObsSpecs(o)
  const thA=(specAfter.thicknesses?.length?specAfter.thicknesses:[specAfter.barThickness??0.10])
  const gpA=specAfter.gaps||[]
  const totA=sum(thA)+sum(gpA)
  const lastA=thA[thA.length-1]||0
  const offA=totA/2-lastA/2
  const fxA=o.xm+nx*offA, fyA=o.ym+ny*offA
  const dx=fxB-fxA, dy=fyB-fyA
  o.xm+=dx; o.ym+=dy
}


// === helpers de âncora (entrada) ===
function frontUnit(obs, dir) {
  const ang  = (obs.rot - 90) * Math.PI / 180
  const sign = (dir === -1 ? -1 : 1)
  return { x: Math.cos(ang) * sign, y: Math.sin(ang) * sign }
}

function entryBarCenter(obs, spec) {
  const th = (spec.thicknesses?.length ? spec.thicknesses : [spec.barThickness ?? 0.10])
  const gp = spec.gaps || []
  if (!th.length) return { x: obs.xm, y: obs.ym }
  const sum = (a)=> a.reduce((acc,v)=> acc + (+v||0), 0)
  const total = sum(th) + sum(gp)
  const first = th[0] || 0
  const off = -total/2 + first/2
  const n = frontUnit(obs, spec.dir)
  return { x: obs.xm + n.x*off, y: obs.ym + n.y*off }
}


/* ===================== Helpers de Parâmetros por obstáculo ===================== */
function getTypeParams(type){ return params.obstacles[type] }

// Medição: deslocamento centro → face de salto (depende das espessuras/vãos)
function measureOffsetObs(obs){
  const spec = getObsSpecs(obs)
  const sum = (arr) => (Array.isArray(arr) ? arr.reduce((a,b)=>a+(+b||0),0) : 0)
  const total = sum(spec.thicknesses || []) + sum(spec.gaps || [])
  return total / 2
}

// Usado em vários lugares
function obstacleEdgePoint(obs, isExit) {
  const n = frontNormal(obs)
  const off = measureOffsetObs(obs) * (isExit ? +1 : -1)
  const spec = getObsSpecs(obs)
  return { x: obs.xm + n.x * off, y: obs.ym + n.y * off, wm: spec.wm, hm: spec.hm }
}

function reflowComboFromChanged(changed) {
  if (!changed?.comboGroup) return

  const parts = state.obstacles.filter(o => o.comboGroup === changed.comboGroup)
  if (parts.length < 2) return

  // eixo do combo (projeta ao longo da frente do que foi alterado)
  const n = frontNormal(changed)
  const proj = (o) => o.xm * n.x + o.ym * n.y
  const ordered = parts.slice().sort((a,b) => proj(a) - proj(b))
  const idx = ordered.findIndex(p => p.id === changed.id)
  if (idx < 0) return

  // faces alvo por par (se não houver meta, usa defaultCentro)
  const faces = getComboFaces(changed.comboGroup, ordered.length - 1)
  const needCC = (A, B, iPair) => faces[iPair] + measureOffsetObs(A) + measureOffsetObs(B)

  // lado “para frente” (+n): fixa o vizinho e empurra os próximos
  for (let j = idx + 1; j < ordered.length; j++) {
    const P = ordered[j - 1] // anterior (fixo)
    const Q = ordered[j]     // mover
    const dcc = needCC(P, Q, j - 1)
    Q.xm = P.xm + n.x * dcc
    Q.ym = P.ym + n.y * dcc
  }
  // lado “para trás” (−n): fixa o vizinho e puxa os anteriores
  for (let j = idx - 1; j >= 0; j--) {
    const Q = ordered[j + 1] // seguinte (fixo)
    const P = ordered[j]     // mover
    const dcc = needCC(P, Q, j)
    P.xm = Q.xm - n.x * dcc
    P.ym = Q.ym - n.y * dcc
  }
}

// projeta no eixo do combo (usa a frente do nó referência)
function _axisFromRef(refObs) {
  const spec = getObsSpecs(refObs)
  const ang  = (refObs.rot - 90) * Math.PI / 180
  const sign = (spec.dir === -1 ? -1 : 1)
  const n    = { x: Math.cos(ang) * sign, y: Math.sin(ang) * sign }
  const proj = (o) => o.xm * n.x + o.ym * n.y
  return { n, proj }
}

function _halfDepth(o) {
  const s = getObsSpecs(o)
  const sum = (a) => (Array.isArray(a) ? a.reduce((x,y)=> x + (+y||0), 0) : 0)
  return (sum(s.thicknesses) + sum(s.gaps)) / 2
}

// Deriva as distâncias FACE-a-FACE a partir das posições atuais do combo
function deriveComboFacesFromGeometry(groupId, refObs) {
  const parts = state.obstacles.filter(o => o.comboGroup === groupId)
  if (parts.length < 2) return []

  // eixo do combo a partir do obstáculo de referência
  const { proj } = _axisFromRef(refObs ?? parts[0])
  const ordered = parts.slice().sort((a,b) => proj(a) - proj(b))

  const faces = []
  for (let i = 0; i < ordered.length - 1; i++) {
    const A = ordered[i], B = ordered[i+1]
    const cc = proj(B) - proj(A)                 // centro-a-centro medido no eixo
    const face = cc - _halfDepth(A) - _halfDepth(B)
    faces.push(Math.max(0, face))                // clamp simples
  }
  return faces
}


/* ===================== Computed ===================== */

// const freeHandlesActive = computed(() => state.arenaMode !== 'rect' && state.free.showHandles)

const overlaysBlocked = computed(() =>
  (state.arenaMode !== 'rect' && state.free.showHandles) || state.tools.measureActive
)

const arenaGeom = computed(() => {
  if (state.arenaMode === 'rect') {
    return {
      mode: 'rect',
      poly: [
        { x: 0, y: 0 },
        { x: state.arenaWm, y: 0 },
        { x: state.arenaWm, y: state.arenaHm },
        { x: 0, y: state.arenaHm }
      ],
      bounds: { width: state.arenaWm, height: state.arenaHm },
      valid: true, err: null, offset: { x: 0, y: 0 }
    }
  }

  // Livre (4 vértices)
  const raw = (state.free?.verts || []).slice(0, 4)
  if (raw.length !== 4) {
    return { mode: 'free', poly: [], bounds: { width: 1, height: 1 }, valid: false, err: 'Faltam vértices.', offset: {x:0,y:0} }
  }

  // Validação simples
  for (let i = 0; i < 4; i++) {
    for (let j = i + 1; j < 4; j++) {
      if (Math.hypot(raw[i].x - raw[j].x, raw[i].y - raw[j].y) < 1e-6) {
        return { mode:'free', poly:[], bounds:{width:1,height:1}, valid:false, err:'Vértices coincidentes.', offset:{x:0,y:0} }
      }
    }
  }

  const cx = (raw[0].x + raw[1].x + raw[2].x + raw[3].x) / 4
  const cy = (raw[0].y + raw[1].y + raw[2].y + raw[3].y) / 4
  const sorted = raw.slice().sort((a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx))

  const minX = Math.min(...sorted.map(p => p.x))
  const minY = Math.min(...sorted.map(p => p.y))
  const poly = sorted.map(p => ({ x: p.x - minX, y: p.y - minY }))

  const maxX = Math.max(...poly.map(p => p.x))
  const maxY = Math.max(...poly.map(p => p.y))
  const bounds = { width: Math.max(1, maxX), height: Math.max(1, maxY) }

  const cross = (a,b,c) => (b.x-a.x)*(c.y-a.y) - (b.y-a.y)*(c.x-a.x)
  const segInt = (a,b,c,d) => {
    const c1 = cross(a,b,c), c2 = cross(a,b,d), c3 = cross(c,d,a), c4 = cross(c,d,b)
    return (c1*c2 < 0) && (c3*c4 < 0)
  }
  const P = poly
  const intersects = segInt(P[0],P[1],P[2],P[3]) || segInt(P[1],P[2],P[3],P[0])
  if (intersects) {
    return { mode:'free', poly:[], bounds, valid:false, err:'Polígono cruza a si mesmo.', offset:{x:minX,y:minY} }
  }

  return { mode:'free', poly, bounds, valid:true, err:null, offset:{ x: minX, y: minY } }
})

// [RULER-COMPUTED] — lista de marcações (em metros)
const arenaRulers = computed(() => {
  const step = Math.max(1, Math.round(params.visual.rulerStep || 10));
  const W = Number(state.arenaWm) || 0;
  const H = Number(state.arenaHm) || 0;

  const marksX = [];
  const marksY = [];
  for (let x = step; x <= W; x += step) marksX.push(x); // 10, 20, 30... até W
  for (let y = step; y <= H; y += step) marksY.push(y); // 10, 20, 30... até H

  return { marksX, marksY };
});

const arenaPx = computed(() => ({
  w: Math.round((arenaGeom.value.bounds.width)  * state.scale),
  h: Math.round((arenaGeom.value.bounds.height) * state.scale),
}))

const arenaStyle = computed(() => ({
  width: Math.round(arenaPx.value.w * state.view.scale) + 'px',
  height: Math.round(arenaPx.value.h * state.view.scale) + 'px',
}))

const viewStyle = computed(() => ({
  transform: `translate(${state.view.x}px, ${state.view.y}px) scale(${state.view.scale})`,
  transformOrigin: '0 0'
}))

const gridStyle = computed(() => {
  const s = state.scale
  return { backgroundSize: `${s}px ${s}px` }
})

/* ===================== Curso (cálculo das curvas) ===================== */
function frontNormal(obs) {
  const ang = (obs.rot - 90) * Math.PI / 180
  const s = (getObsSpecs(obs).dir === -1 ? -1 : 1)
  return { x: Math.cos(ang) * s, y: Math.sin(ang) * s }
}


const courseSegments = computed(() => {
  // garante reatividade quando mexer nesses knobs, mesmo que a lista do curso não mude
  // eslint-disable-next-line no-unused-vars
const __deps = [
    params.path.pathTension,
    params.path.curveSmoothness,
    params.path.exitStrides,
    params.path.entryStrides,
    params.path.rMin,
    params.path.clearance,
    params.visual.preferGlobalRMin   // <— novo
  ];

  const segs = []
  const courseList = state.tools.course
  if (!courseList || courseList.length < 2) return segs

  function makeSegment(A, B, guard = 0) {
    if (guard > 3) return // evita loop infinito

    const segA = state.tools.course.find(c => c.id === A.id)
    const segB = state.tools.course.find(c => c.id === B.id)

    const P0 = obstacleEdgePoint(A, true)
    const P3 = obstacleEdgePoint(B, false)

    const dx = P3.x - P0.x
    const dy = P3.y - P0.y
    const dist = Math.hypot(dx, dy)
    if (dist < 0.001) return

    const dir = { x: dx / dist, y: dy / dist }
    const nA  = frontNormal(A)
    const nB  = frontNormal(B)

    const STRIDE = 3.5
    let wantOut = ((segA?.exitStrides) ?? params.path.exitStrides) * STRIDE
    let wantIn  = ((segB?.entryStrides) ?? params.path.entryStrides) * STRIDE

    // Sem checkboxes: usa local se houver; senão, herda globais do Modal
    const t     = (typeof segA?.pathTension     === 'number') ? segA.pathTension     : params.path.pathTension
    const s     = (typeof segA?.curveSmoothness === 'number') ? segA.curveSmoothness : params.path.curveSmoothness
    const R_MIN = (typeof segA?.rMin            === 'number') ? segA.rMin            : params.path.rMin

    // Regras especiais Start/Finish
    const isStart = A.type === 'start'
    const isFinish = B.type === 'finish'
    if (isStart) {
      wantOut = Math.max(wantOut, params.obstacles.start.minOutStraight || 0)
    }
    if (isFinish) {
      wantIn  = Math.max(wantIn,  params.obstacles.finish.minInStraight || 0)
    }

    const capEach = dist * 0.48
    wantOut = Math.min(wantOut, capEach)
    wantIn  = Math.min(wantIn,  capEach)

    // Ajuste adaptativo p/ distâncias curtas
    // let distFactor = 1
    // if (dist < 15) {
    //   distFactor = Math.max(0.4, dist / 15)
    //   wantOut *= distFactor
    //   wantIn  *= distFactor
    //   const extraSmooth = (1 - distFactor) * 0.4
    //   s = clamp(s + extraSmooth, 0, 1)
    //   const tensionReduce = (1 - distFactor) * 0.3
    //   t = clamp(t - tensionReduce, 0, 1)
    // }

    const R1 = { x: P0.x + nA.x * wantOut, y: P0.y + nA.y * wantOut }
    const R2 = { x: P3.x - nB.x * wantIn,  y: P3.y - nB.y * wantIn }

    const blendFactor = 0.7 * t + 0.3 * s
    let blendOut = 0.05 + blendFactor * 0.25
    let blendIn  = 0.10 + blendFactor * 0.30
    if (isStart) blendOut = 0
    if (isFinish) blendIn = 0

    const norm = v => {
      const m = Math.hypot(v.x, v.y) || 1e-6
      return { x: v.x/m, y: v.y/m }
    }

    const hOut = norm({ x: (1-blendOut)*nA.x + blendOut*dir.x,
                        y: (1-blendOut)*nA.y + blendOut*dir.y })
    const inDir = { x: -dir.x, y: -dir.y }
    const hIn  = norm({ x: (1-blendIn)*(-nB.x) + blendIn*inDir.x,
                        y: (1-blendIn)*(-nB.y) + blendIn*inDir.y })

    let lenA = Math.max(wantOut, dist * 0.20)
    let lenB = Math.max(wantIn,  dist * 0.25)
    let maxHandle = Math.min(dist * 1.0, 14.0)

    // Ajuste de raio mínimo simétrico
    let maxIterations = 12
    for (let k = 0; k < maxIterations; k++) {
      const c1Test = { x: R1.x + hOut.x * lenA, y: R1.y + hOut.y * lenA }
      const c2Test = { x: R2.x + hIn.x * lenB, y: R2.y + hIn.y * lenB }
      const r0 = radiusAtStart(R1, c1Test, c2Test)
      const r1 = radiusAtEnd(c1Test, c2Test, R2)
      const minR = Math.min(r0, r1)
      if (minR >= R_MIN - 0.05) break
      const factor = (R_MIN / (minR || 1e-6))
      lenA = clamp(lenA * factor, wantOut * 0.5, maxHandle)
      lenB = clamp(lenB * factor, wantIn * 0.5, maxHandle)
    }

    const C1 = { x: R1.x + hOut.x * lenA, y: R1.y + hOut.y * lenA }
    const C2 = { x: R2.x + hIn.x * lenB, y: R2.y + hIn.y * lenB }

    // Detecção de colisão (usa folga configurável)
    const hitLine1 = lineHitsAnyObstacle(P0, R1, [A.id, B.id])
    const hitLine2 = lineHitsAnyObstacle(R2, P3, [A.id, B.id])
    const hitCurve = bezierHitsAnyObstacle(R1, C1, C2, R2, [A.id, B.id])
    const hit = hitLine1 || hitCurve || hitLine2

    if (hit) {
      const via = computeDetourPoint(A, B, hit)
      const fake = { id: -1, xm: via.x, ym: via.y, rot: Math.atan2(dy, dx) * 180/Math.PI, type: 'vertical' }
      makeSegment(A, fake, guard + 1)
      makeSegment(fake, B, guard + 1)
      return
    }

    // Label
    const mid = cubicPointAt(0.5, R1, C1, C2, R2)
    const tan = cubicTangentAt(0.5, R1, C1, C2, R2)
    const tlen = Math.hypot(tan.x, tan.y) || 1e-6
    const nx = -tan.y / tlen, ny = tan.x / tlen
    const offM = 8 / state.scale
    const label = { x: mid.x + nx * offM, y: mid.y + ny * offM }

    const raioDebug = Math.min(
      radiusAtStart(R1, C1, C2),
      radiusAtEnd(C1, C2, R2)
    )

    segs.push({
      key: `${A.id}-${B.id}-${guard}`,
      p0: P0, r1: R1, c1: C1, c2: C2, r2: R2, p3: P3,
      dist, label, raio: raioDebug
    })
  }

  for (let i = 0; i < courseList.length - 1; i++) {
    const A = state.obstacles.find(o => o.id === courseList[i].id)
    const B = state.obstacles.find(o => o.id === courseList[i + 1].id)
    if (A && B) makeSegment(A, B, 0)
  }
  return segs
})

/* ===== Tempo do Percurso (TA/TL) — mínimo ===== */
const speedPreset   = ref(350);   // 300/325/350/375 ou -1 para personalizada
const speedCustom   = ref(350);
const speedMPerMin  = computed(() =>
  speedPreset.value === -1 ? Number(speedCustom.value || 0) : Number(speedPreset.value || 0)
);

// Comprimento por segmento (linhas + curva Bézier)
// (reaproveita sua cubicPointAt já definida acima)
function bezierLength(P0, C1, C2, P3, steps = 32) {
  let L = 0, prev = P0;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const pt = cubicPointAt(t, P0, C1, C2, P3);
    L += Math.hypot(pt.x - prev.x, pt.y - prev.y);
    prev = pt;
  }
  return L;
}

const courseMeters = computed(() => {
  const segs = courseSegments.value || [];
  let total = 0;
  for (const seg of segs) {
    total += Math.hypot(seg.p0.x - seg.r1.x, seg.p0.y - seg.r1.y);
    total += bezierLength(seg.r1, seg.c1, seg.c2, seg.r2, 32);
    total += Math.hypot(seg.r2.x - seg.p3.x, seg.r2.y - seg.p3.y);
  }
  return total;
});

const taSec  = computed(() => {
  const v = speedMPerMin.value, L = courseMeters.value;
  return v > 0 ? (L / v) * 60 : 0;           // segundos
});
const tlSec  = computed(() => taSec.value * 2);
const optSec = computed(() => Math.max(taSec.value - 4, 0));

function fmtSec(s) {
  const v = Math.max(0, Number(s) || 0);
  const m = Math.floor(v / 60);
  const sec = Math.round(v - m * 60);
  return `${m}:${String(sec).padStart(2, '0')}s`;
}

/* ===================== Helpers ===================== */

function addObstacle(type, evt) {
  historyPush('addObstacle')
  const p  = getTypeParams(type)
  const xm = (arenaGeom.value.bounds.width)  / 2
  const ym = (arenaGeom.value.bounds.height) / 2
  const obs = { id: idSeq++, type, xm, ym, rot: 0, z: Date.now(), label: String(state.nextLabel++), dir: p.dir, local: {} }

  // Se ALT estiver pressionado ao clicar, abre prompt para alturas
  if ((type === 'oxer' || type === 'tripleBar') && evt?.altKey) {
    const needBars = (type === 'oxer') ? 2 : 3
    const defHs = (p.barHeights?.length === needBars) ? p.barHeights
                : Array(needBars).fill(1.0).map((v,i)=> +(v + i*0.2).toFixed(2))
    const ans = window.prompt(
      `Alturas das barras (m), ${needBars} valores separados por ponto e vírgula (;)`,
      formatListSemicolon(defHs, 2)
    );
    if (ans != null) {
      const arr = parseNumberList(ans)
      if (arr.length === needBars) {
        obs.local.barHeights  = arr
        obs.local.thicknesses = [...p.thicknesses]
      }
    }
  }

  state.obstacles.push(obs)
  state.selectedId = obs.id
}

// ===================== Helpers de clonagem e aplicação =====================
function deepClone(obj) {
  // para snapshots simples, JSON é suficiente (sem funções/dates)
  return JSON.parse(JSON.stringify(obj))
}

function pickExisting(src, keys) {
  const out = {}
  for (const k of keys) if (k in src) out[k] = deepClone(src[k])
  return out
}

function assignExisting(dst, src) {
  // copia apenas chaves que já existem em dst (preserva reatividade)
  for (const k of Object.keys(src || {})) {
    if (k in dst) {
      const val = src[k]
      if (Array.isArray(dst[k]) && Array.isArray(val)) {
        dst[k].splice(0, dst[k].length, ...val)
      } else if (dst[k] && typeof dst[k] === 'object' && val && typeof val === 'object') {
        assignExisting(dst[k], val)
      } else {
        dst[k] = val
      }
    }
  }
}

function isRefLike(x) { return x && typeof x === 'object' && 'value' in x }

// ===================== Sanitização dos pedaços =====================

// Liste aqui os campos do "state" que você quer snapshottar.
// Ajuste os nomes conforme o seu state real:
const STATE_KEYS_TOP = [
  // dimensões/modo/escala/grades/overlays:
  'arenaMode', 'arenaWm', 'arenaHm',
  'scale', 'gridStepM', 'snapGrid', 'snapAngle', 'showGrid', 'showRuler',
  // câmera/viewport (se existir):
  'view', // pan/zoom/scale; se for objeto, abaixo já tratamos
  // ferramentas/rotas (se forem objetos):
  'tools', 'route',
  // seleção (se você guarda o id no state):
  'selectedId'
]

// Se seu "view" for um objeto com pan/zoom:
const VIEW_KEYS = ['x', 'y', 'scale', 'panX', 'panY', 'zoom'] // mantenha os que existirem

// Se seu "tools" tem raio mínimo e outras flags:
const TOOLS_KEYS = ['rMin', 'snapToArenaEdge', 'snapToGrid', 'snapToAngles'] // ajuste aos seus nomes

// Se seu "route" é um objeto com segmentos/handles:
const ROUTE_KEYS = ['segments', 'settings', 'closed'] // ajuste conforme sua estrutura

// Quais propriedades guardar de cada obstáculo:
function serializeObstacle(o) {
  // Mapeia campos comuns; adapte aos seus:
  const keys = [
    'id', 'type',
    'x', 'y', 'xm', 'ym',      // use o que existir no seu objeto
    'rot', 'w', 'h',
    'face', 'flipped', 'locked', 'hidden',
    'entry', 'exit',           // se você salva pontos de entrada/saída
    'local', 'spec'            // configs locais (espessuras, alturas, vãos)
  ]
  const base = pickExisting(o, keys)
  return base
}

// Serialização completa do state para snapshot:
function serializeState() {
  const s = {}

  // top-level selecionados
  Object.assign(s, pickExisting(state, STATE_KEYS_TOP))

  // detalhamento seguro de sub-objetos conhecidos:
  if (state.view && typeof state.view === 'object') {
    s.view = pickExisting(state.view, VIEW_KEYS.filter(k => k in state.view))
  }
  if (state.tools && typeof state.tools === 'object') {
    s.tools = pickExisting(state.tools, TOOLS_KEYS.filter(k => k in state.tools))
  }
  if (state.route && typeof state.route === 'object') {
    s.route = pickExisting(state.route, ROUTE_KEYS.filter(k => k in state.route))
  }

  // Obstáculos (array)
  if (Array.isArray(state.obstacles)) {
    s.obstacles = state.obstacles.map(serializeObstacle)
  }

  // Se você não tem state.selectedId, mas tem "selected" como ref/computed:
  // tente capturar o id atual.
  try {
    if (!('selectedId' in s)) {
      const sel = unref(selected) // lida com ref/computed/valor direto
      s.selectedId = sel?.id ?? null
    }
  } catch (err) {
    // ok: selected pode não existir neste contexto de snapshot
  }
  return s
}

// Serialização dos params (defaults/base de tipos de obstáculos)
function serializeParams() {
  // params pode ser reactive(obj) ou ref(obj)
  const src = isRefLike(params) ? params.value : params
  return deepClone(src)
}

// ===================== Adaptadores da História =====================

function getSnapshot() {
  return {
    version: 1,   // útil para evoluir estrutura no futuro
    state: serializeState(),
    params: serializeParams()
  }
}

function applySnapshot(snap) {
  if (!snap) return

  // 1) aplica state
  if (snap.state && typeof snap.state === 'object') {
    // top-level
    assignExisting(state, snap.state)

    // view
    if (state.view && snap.state.view) assignExisting(state.view, snap.state.view)

    // tools
    if (state.tools && snap.state.tools) assignExisting(state.tools, snap.state.tools)

    // route: pode ser objeto ou array no seu projeto
    if (state.route && snap.state.route) {
      if (Array.isArray(state.route) && Array.isArray(snap.state.route)) {
        state.route.splice(0, state.route.length, ...snap.state.route)
      } else if (!Array.isArray(state.route) && typeof snap.state.route === 'object') {
        assignExisting(state.route, snap.state.route)
      }
    }

    // obstacles (sempre array)
    if (Array.isArray(state.obstacles) && Array.isArray(snap.state.obstacles)) {
      state.obstacles.splice(0, state.obstacles.length, ...snap.state.obstacles)
    }

    // selectedId (se existir no seu state)
    if ('selectedId' in state && 'selectedId' in snap.state) {
      state.selectedId = snap.state.selectedId
    }
  }

  // 2) aplica params
  if (snap.params) {
    if (isRefLike(params)) {
      assignExisting(params.value, snap.params)
    } else if (params && typeof params === 'object') {
      assignExisting(params, snap.params)
    }
  }
}

const history = useHistory(getSnapshot, applySnapshot, { limit: 80 })

/* ===================== Histórico (Undo/Redo) ===================== */

function snapState() {
  return {
    params: structuredClone(params),
    scale: state.scale,
    arenaWm: state.arenaWm,
    arenaHm: state.arenaHm,
    snap: state.snap,
    selectedId: state.selectedId,
    obstacles: state.obstacles.map(o => ({ ...o, local: structuredClone(o.local || {}) })),
    nextLabel: state.nextLabel,
    view: { ...state.view },
    tools: {
      ...state.tools,
      measurePts: state.tools.measurePts.map(p => ({ ...p })),
      course: state.tools.course.map(c => ({ ...c })),
    },
    idSeq
  }
}

function applySnap(s) {
  Object.assign(params, structuredClone(s.params || params))
  state.scale   = s.scale
  state.arenaWm = s.arenaWm
  state.arenaHm = s.arenaHm
  state.snap    = s.snap
  state.selectedId = s.selectedId
  state.obstacles  = s.obstacles.map(o => ({ ...o, local: structuredClone(o.local || {}) }))
  state.nextLabel  = s.nextLabel
  Object.assign(state.view, s.view || {})

  Object.assign(state.tools, {
    measureActive: s.tools.measureActive,
    measurePts: (s.tools.measurePts || []).map(p => ({ ...p })),
    pathActive: s.tools.pathActive,
    course: (s.tools.course || []).map(c => ({ ...c })),
    clickMode: s.tools.clickMode,
    pathTension: s.tools.pathTension,
    curveSmoothness: s.tools.curveSmoothness,
    exitStrides: s.tools.exitStrides,
    entryStrides: s.tools.entryStrides,
    rMin: s.tools.rMin
  })

  idSeq = s.idSeq ?? idSeq
}

const _history = useHistory(snapState, applySnap, { limit: 80 })

function historyPush(label = '') { _history.push(label) }
function undo() { _history.undo() }
function redo() { _history.redo() }


/* ===================== Teclado ===================== */
function onKey(e) {
  // se está digitando em um campo, não dispara atalhos
  const t   = e.target
  const tag = (t?.tagName || '').toLowerCase()
  if (paramsModal.open || t?.isContentEditable || ['input','textarea','select'].includes(tag)) {
    return
  }

  const key = e.key.toLowerCase()

  // Undo / Redo
  if ((e.ctrlKey || e.metaKey) && !e.shiftKey && key === 'z') {
    e.preventDefault(); undo(); return
  }
  if ((e.ctrlKey || e.metaKey) && (key === 'y' || (e.shiftKey && key === 'z'))) {
    e.preventDefault(); redo(); return
  }

  if (!selected.value) return
  const o = selected.value
  const step = e.shiftKey ? 1 : state.moveStep

  switch (key) {
    case 'delete':
    case 'backspace':
      e.preventDefault()
      historyPush('remove')
      removeSelected()
      break
    case 'f': {
      e.preventDefault()
      o.local = o.local || {}
      const currentDir = getObsSpecs(o).dir
      const newDir = (currentDir === -1 ? 1 : -1)
      o.local.dir = newDir
      break
    }
    case 'd':
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        historyPush('dup')
        duplicateSelected()
      }
      break
    case 'arrowup':
      e.preventDefault()
      o.ym = clamp(o.ym - step, 0, state.arenaHm)
      break
    case 'arrowdown':
      e.preventDefault()
      o.ym = clamp(o.ym + step, 0, state.arenaHm)
      break
    case 'arrowleft':
      e.preventDefault()
      o.xm = clamp(o.xm - step, 0, state.arenaWm)
      break
    case 'arrowright':
      e.preventDefault()
      o.xm = clamp(o.xm + step, 0, state.arenaWm)
      break
  }
}

function centerAll() {
  historyPush('centerAll');
  const cx = (arenaGeom.value.bounds.width) / 2
  const cy = (arenaGeom.value.bounds.height) / 2
  state.obstacles.forEach(o => { o.xm = cx; o.ym = cy })
}

function removeSelected() {
  if (!selected.value) return
  historyPush('remove');
  state.tools.course = state.tools.course.filter(c => c.id !== state.selectedId)
  state.obstacles = state.obstacles.filter(o => o.id !== state.selectedId)
  state.selectedId = null
}

function duplicateSelected() {
  if (!selected.value) return
  historyPush('duplicate');
  const o = selected.value
  const copy = { ...o, id: idSeq++, xm: clamp(o.xm + 1, 0, state.arenaWm), ym: clamp(o.ym + 1, 0, state.arenaHm), z: Date.now(), label: String(state.nextLabel++) }
  // deep clone do local
  copy.local = structuredClone(o.local || {})
  state.obstacles.push(copy)
  state.selectedId = copy.id
}

function exportJSON() {
  const data = JSON.stringify({
    params: structuredClone(params),
    arena: {
      mode: state.arenaMode,
      w: state.arenaWm, h: state.arenaHm,
      free: state.free
    },
    obstacles: state.obstacles.map(o => ({ ...o, local: structuredClone(o.local || {}) })),
    course: state.tools.course,
    view: state.view,
  }, null, 2)
  const blob = new Blob([data], { type: 'application/json' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = 'pista-hipismo.json'
  a.click()
  URL.revokeObjectURL(a.href)
}

function onImport(e) {
  const file = e.target.files?.[0]
  if (!file) return
  const reader = new FileReader()
  reader.onload = () => {
    historyPush('import')
    try {
      const json = JSON.parse(reader.result)

      // Parâmetros globais (retrocompatível: se não vier, mantém atuais)
      if (json.params) {
        Object.assign(params, json.params)
        // Update ferramentas com globais
        state.tools.pathTension   = params.path.pathTension
        state.tools.curveSmoothness = params.path.curveSmoothness
        state.tools.exitStrides   = params.path.exitStrides
        state.tools.entryStrides  = params.path.entryStrides
        state.tools.rMin          = params.path.rMin
        state.scale               = params.arena.scale
      }

      state.arenaMode = json.arena?.mode ?? state.arenaMode
      state.arenaWm   = json.arena?.w ?? state.arenaWm
      state.arenaHm   = json.arena?.h ?? state.arenaHm
      state.arena = json.arena ?? { mode: 'rect' }
      state.free  = json.arena?.free ?? state.free
      if (!state.free.verts || state.free.verts.length !== 4) {
        state.free.verts = [
          { x: 0, y: 0 }, { x: state.arenaWm, y: 0 },
          { x: state.arenaWm, y: state.arenaHm }, { x: 0, y: state.arenaHm }
        ]
      }

      // Obstáculos (mantém overrides locais)
      state.obstacles = (json.obstacles || []).map((o, idx) => ({
        ...o,
        id: Number(o.id) || (idx + 1),
        z: o.z ?? Date.now() + idx,
        local: structuredClone(o.local || {})
      }))

      // Curso
      if (Array.isArray(json.course)) {
        state.tools.course = json.course.map(c => {
          if (typeof c === 'number') {
            return { id: c, exitStrides: state.tools.exitStrides, entryStrides: state.tools.entryStrides }
          }
          return {
            id: Number(c.id),
            pathTension: c.pathTension ?? state.tools.pathTension,
            curveSmoothness: c.curveSmoothness ?? state.tools.curveSmoothness,
            exitStrides: c.exitStrides ?? state.tools.exitStrides,
            entryStrides: c.entryStrides ?? state.tools.entryStrides,
            rMin: c.rMin ?? state.tools.rMin
          }
        })
      }

      // view
      state.view = { ...state.view, ...(json.view || {}) }

      // seleção/labels
      state.selectedId = null
      const nums = state.obstacles.map(o => parseInt(o.label, 10)).filter(n => !isNaN(n))
      state.nextLabel = nums.length ? Math.max(...nums) + 1 : state.obstacles.length + 1

      // ids
      recomputeIds()

    } catch (err) {
      alert('JSON inválido')
    }
  }
  reader.readAsText(file)
  e.target.value = ''
}

function clearAll() {
  if (confirm('Limpar todos os obstáculos?')) {
    historyPush('clearAll')
    state.obstacles = []
    state.tools.course = []
    state.tools.measurePts = []
    state.selectedId = null
    state.nextLabel = 1
    idSeq = 1
  }
}

// helper: garantir ids únicos e atualizar idSeq
function recomputeIds() {
  state.obstacles.forEach(o => { o.id = Number(o.id) || 0 })
  const maxId = state.obstacles.length ? Math.max(...state.obstacles.map(o => o.id)) : 0
  idSeq = maxId + 1
}

/* ===================== Colisão / Folga ===================== */
function clearanceM(){ return params.path.clearance || 1.5 }

function pointInRotRect(px, py, cx, cy, w, h, rotDeg) {
  const ang = rotDeg * Math.PI / 180
  const cos = Math.cos(-ang), sin = Math.sin(-ang)
  const dx = px - cx, dy = py - cy
  const rx = dx * cos - dy * sin
  const ry = dx * sin + dy * cos
  return Math.abs(rx) <= w/2 && Math.abs(ry) <= h/2
}

function bezierHitsAnyObstacle(P0, C1, C2, P3, ignoreIds = []) {
  const steps = 64
  for (let t = 0; t <= 1.00001; t += 1/steps) {
    const p = cubicPointAt(t, P0, C1, C2, P3)
    for (const o of state.obstacles) {
      if (ignoreIds.includes(o.id)) continue
      const spec = getObsSpecs(o)
      const w = spec.wm + clearanceM()
      const h = spec.hm + clearanceM()
      if (pointInRotRect(p.x, p.y, o.xm, o.ym, w, h, o.rot)) {
        return o
      }
    }
  }
  return null
}

function lineHitsAnyObstacle(A, B, ignoreIds = []) {
  const steps = 32
  for (let i = 0; i <= steps; i++) {
    const t = i / steps
    const p = { x: A.x + (B.x - A.x) * t, y: A.y + (B.y - A.y) * t }
    for (const o of state.obstacles) {
      if (ignoreIds.includes(o.id)) continue
      const spec = getObsSpecs(o)
      const w = spec.wm + clearanceM()
      const h = spec.hm + clearanceM()
      if (pointInRotRect(p.x, p.y, o.xm, o.ym, w, h, o.rot)) return o
    }
  }
  return null
}

// waypoint de desvio
function computeDetourPoint(A, B, hitObs) {
  const P0 = obstacleEdgePoint(A, true)
  const P3 = obstacleEdgePoint(B, false)
  const vx = P3.x - P0.x, vy = P3.y - P0.y
  const d  = Math.hypot(vx, vy) || 1e-6
  const dir = { x: vx/d, y: vy/d }

  const left  = { x: -dir.y, y:  dir.x }
  const right = { x:  dir.y, y: -dir.x }

  const spec = getObsSpecs(hitObs)
  const halfW = (spec.wm + clearanceM()) / 2
  const halfH = (spec.hm + clearanceM()) / 2
  let   margin = Math.max(halfW, halfH) + clearanceM()

  const nHit = frontNormal(hitObs)
  const headOn = Math.abs(nHit.x*dir.x + nHit.y*dir.y)
  margin += clearanceM() * (0.5 + 0.5*headOn)

  const cand = [
    { side:'L', p:{ x: hitObs.xm + left.x  * margin, y: hitObs.ym + left.y  * margin } },
    { side:'R', p:{ x: hitObs.xm + right.x * margin, y: hitObs.ym + right.y * margin } },
  ]

  const pushOut = (p, axis) => {
    let m = margin, q = { ...p }, tries = 0
    while (
      pointInRotRect(q.x, q.y,
        hitObs.xm, hitObs.ym,
        spec.wm + clearanceM(), spec.hm + clearanceM(), hitObs.rot
      ) && tries < 4
    ) {
      m *= 1.25
      q = { x: hitObs.xm + axis.x * m, y: hitObs.ym + axis.y * m }
      tries++
    }
    return q
  }
  cand[0].p = pushOut(cand[0].p, left)
  cand[1].p = pushOut(cand[1].p, right)

  const score = (p) => {
    const a1 = Math.atan2(p.y - P0.y, p.x - P0.x)
    const a2 = Math.atan2(P3.y - p.y, P3.x - p.x)
    let s = Math.abs(a2 - a1)

    const v = { x: p.x - hitObs.xm, y: p.y - hitObs.ym }
    const vv = Math.hypot(v.x, v.y) || 1e-6
    const vn = { x: v.x/vv, y: v.y/vv }
    const frontDot = vn.x * nHit.x + vn.y * nHit.y
    if (frontDot > 0) s += 1000

    return s
  }

  const s0 = score(cand[0].p)
  const s1 = score(cand[1].p)
  return s0 <= s1 ? cand[0].p : cand[1].p
}

/* ===================== Arena: pan/zoom ===================== */
let spacePressed = false

function onArenaDown(ev) {

  // --- MEDIÇÃO TEM PRIORIDADE ---
  if (state.tools.measureActive) {
    const p = pointerToArenaMeters(ev)
    const pts = state.tools.measurePts

    if (pts.length === 0 || pts.length >= 2) {
      // 1º clique (ou recomeça nova medição)
      state.tools.measurePts = [p]
      state.tools.measureHover = p
      window.addEventListener('pointermove', onMeasureMove)
    } else {
      // 2º clique: fixa e para de seguir o mouse
      state.tools.measurePts.push(p)
      state.tools.measureHover = null
      window.removeEventListener('pointermove', onMeasureMove)
    }
    return // não selecione obstáculos, não faça pan, etc.
  }


  const isMiddle = ev.button === 1
  if (isMiddle || (ev.target && spacePressed)) { startPan(ev); return }

  const path = (ev.composedPath && ev.composedPath()) || ev.path || []
  const overObs = path.some(el => el && el.classList && el.classList.contains('obs'))
  const overHandle = path.some(el => el && el.classList && el.classList.contains('rot-handle'))
  if (overHandle) return

  if (state.tools.measureActive && !overObs) {
    const { xm, ym } = screenToArena(ev.clientX, ev.clientY)
    addMeasurePoint(xm, ym); return
  }

  if (!overObs) state.selectedId = null
}

function startPan(ev) {
  state.view.panning = true
  const { clientX, clientY } = ev
  state.view.panStart = { clientX, clientY, x: state.view.x, y: state.view.y }
  window.addEventListener('pointermove', onPanMove)
  window.addEventListener('pointerup', endPan, { once: true })
}
function onPanMove(ev) {
  if (!state.view.panning) return
  const dx = ev.clientX - state.view.panStart.clientX
  const dy = ev.clientY - state.view.panStart.clientY
  state.view.x = state.view.panStart.x + dx
  state.view.y = state.view.panStart.y + dy
}
function endPan() {
  state.view.panning = false
  window.removeEventListener('pointermove', onPanMove)
}
function onWheel(ev) {
  if (!ev.ctrlKey) return
  ev.preventDefault()
  const prev = state.view.scale
  const factor = ev.deltaY < 0 ? 1.1 : 0.9
  const next = clamp(prev * factor, 0.3, 4)
  const rect = arenaRef.value.getBoundingClientRect()
  const cx = ev.clientX - rect.left - state.view.x
  const cy = ev.clientY - rect.top - state.view.y
  state.view.x -= (cx * next/prev - cx)
  state.view.y -= (cy * next/prev - cy)
  state.view.scale = next
}
// Converte coordenadas de tela -> arena (em METROS)
// Retorna { xm, ym, ok:true } ou { ok:false } se o elemento não estiver pronto.
// Converte coordenadas de tela -> arena (em METROS)
// Retorna { xm, ym, ok:true } ou { ok:false } se o elemento não estiver pronto.
function screenToArena(clientX, clientY) {
  const host = arenaRef?.value || null
  const el   = host?.querySelector?.('svg') || host
  if (!el) return { ok: false }

  const rect = el.getBoundingClientRect()
  const xPx  = clientX - rect.left
  const yPx  = clientY - rect.top

  const s  = state.scale || 1
  const vx = state.view?.x || 0
  const vy = state.view?.y || 0

  const xm = (xPx - vx) / s
  const ym = (yPx - vy) / s
  return { xm, ym, ok: true }
}

/* ===================== Drag & Rotate ===================== */
function onObstacleDown(ev, obs) {
  historyPush('dragStart')
  actions.select(obs.id)
  ev.currentTarget?.setPointerCapture?.(ev.pointerId)

  const s = screenToArena(ev.clientX, ev.clientY)
  const start = { xm0: obs.xm, ym0: obs.ym, xm: s.xm, ym: s.ym }

  // 👉 Se o obstáculo pertence a um combo, movemos o GRUPO todo
  const groupId = obs.comboGroup ?? null
  const groupMembers = groupId
    ? state.obstacles.filter(o => o.comboGroup === groupId)
    : null

  // Guardamos as posições iniciais dos membros (para aplicar o mesmo delta)
  const groupStart = groupMembers
    ? groupMembers.map(o => ({ ref:o, x:o.xm, y:o.ym }))
    : null

  state.drag = {
    active: true, mode: 'move', id: obs.id, start, shift: ev.shiftKey,
    groupId, groupStart
  }

  const move = (e) => {
    if (!state.drag.active || state.drag.mode !== 'move') return
    const cur = screenToArena(e.clientX, e.clientY)
    let dx = cur.xm - state.drag.start.xm
    let dy = cur.ym - state.drag.start.ym

    if (state.drag.groupStart && state.drag.groupStart.length) {
      // ====== CONFINAR O GRUPO ======
      const startPts = state.drag.groupStart.map(p => ({ x:p.x, y:p.y }))

      if (state.arenaMode === 'rect' || !state.free?.confine) {
        const lim = confineDeltaGroupRect(startPts, dx, dy, Number(state.arenaWm||0), Number(state.arenaHm||0))
        dx = lim.dx; dy = lim.dy
      } else {
        const poly = arenaGeom.value?.poly || []
        const lim = confineDeltaGroupPoly(startPts, dx, dy, poly)
        dx = lim.dx; dy = lim.dy
      }

      // aplica o MESMO delta a todos os membros
      for (const p of state.drag.groupStart) {
        p.ref.xm = p.x + dx
        p.ref.ym = p.y + dy
      }
    } else {
      // ====== CASO SIMPLES (obstáculo isolado) ======
      let x = state.drag.start.xm0 + dx
      let y = state.drag.start.ym0 + dy

      if (state.arenaMode === 'rect' || !state.free?.confine) {
        x = clamp(x, 0, Number(state.arenaWm||0))
        y = clamp(y, 0, Number(state.arenaHm||0))
      } else {
        const poly = arenaGeom.value?.poly || []
        // usa o mesmo limitador de grupo com 1 ponto
        const lim = confineDeltaGroupPoly([{x:state.drag.start.xm0,y:state.drag.start.ym0}], dx, dy, poly)
        x = state.drag.start.xm0 + lim.dx
        y = state.drag.start.ym0 + lim.dy
      }

      obs.xm = x
      obs.ym = y
    }
  }

  const up = () => {
    state.drag.active = false
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up, { once:true })
  }

  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', up, { once:true })
}

function onRotateDown(ev, obs) {
  historyPush('rotateStart')
  actions.select(obs.id)
  ev.currentTarget.setPointerCapture(ev.pointerId)

  const comboParts = obs.comboGroup
    ? state.obstacles.filter(o => o.comboGroup === obs.comboGroup)
    : [obs]

  const centerX = comboParts.reduce((sum, p) => sum + p.xm, 0) / comboParts.length
  const centerY = comboParts.reduce((sum, p) => sum + p.ym, 0) / comboParts.length

  const startData = comboParts.map(p => ({
    id: p.id,
    xm: p.xm,
    ym: p.ym,
    rot: p.rot,
    relX: p.xm - centerX,
    relY: p.ym - centerY
  }))

  const rect = arenaRef.value.getBoundingClientRect()
  const x = (ev.clientX - rect.left - state.view.x) / state.view.scale
  const y = (ev.clientY - rect.top - state.view.y) / state.view.scale
  const startAng = Math.atan2(y - centerY * state.scale, x - centerX * state.scale)

  state.drag = {
    active: true,
    mode: 'rotate',
    id: obs.id,
    start: { startAng, centerX, centerY, startData },
    shift: ev.shiftKey
  }
}

function onPointerMove(ev) {
  if (!state.drag.active) return
  const obs = state.obstacles.find(o => o.id === state.drag.id)
  if (!obs) return

  if (state.drag.mode === 'move') {
    const { xm, ym } = screenToArena(ev.clientX, ev.clientY)
    let nx = state.drag.start.xm0 + (xm - state.drag.start.xm)
    let ny = state.drag.start.ym0 + (ym - state.drag.start.ym)

    // === Snap com modificadores ===
    // base da grade
    const baseStep = params.arena.snapGrid || 0.5

    // estado padrão do snap (igual ao botão/flag geral)
    let snapOn   = state.snap
    let snapStep = baseStep

    // Ctrl  -> sem snap (livre)
    // Alt   -> snap mais fino (metade do passo)
    // Ctrl+Alt -> snap mais grosso (passo dobrado)
    if (ev.ctrlKey && ev.altKey) {
      snapOn = true
      snapStep = baseStep * 2
    } else if (ev.ctrlKey) {
      snapOn = false
    } else if (ev.altKey) {
      snapOn = true
      snapStep = Math.max(0.01, baseStep / 2)
    }

    if (snapOn) {
      nx = Math.round(nx / snapStep) * snapStep
      ny = Math.round(ny / snapStep) * snapStep
    }
    // === fim modificadores ===

    if (obs.comboGroup) {
      const comboParts = state.obstacles.filter(o => o.comboGroup === obs.comboGroup)
      const dx = nx - obs.xm
      const dy = ny - obs.ym
      comboParts.forEach(p => { p.xm += dx; p.ym += dy })
    } else {
      if (state.arenaMode === 'rect' && state.free.confine) {
        const poly = arenaGeom.value.poly
        const clamped = clampToPolygon({ x: nx, y: ny }, poly)
        nx = clamped.x; ny = clamped.y
      }
      obs.xm = clamp(nx, 0, state.arenaWm)
      obs.ym = clamp(ny, 0, state.arenaHm)
    }
  }
  else if (state.drag.mode === 'rotate') {
    const { startAng, centerX, centerY, startData } = state.drag.start
    const rect = arenaRef.value.getBoundingClientRect()
    const x = (ev.clientX - rect.left - state.view.x) / state.view.scale
    const y = (ev.clientY - rect.top - state.view.y) / state.view.scale
    let newAng = Math.atan2(y - centerY * state.scale, x - centerX * state.scale)

    let deltaDeg = (newAng - startAng) * (180 / Math.PI)
    deltaDeg = (deltaDeg + 360) % 360

    // ===== Snap de ângulo (padrão + modificadores) =====
    // Padrão: rotSnapDeg (fallback 15°)
    // Alt   → livre
    // Ctrl  → fino (5°)
    // Shift → grosso (45°)
    let step = (params.visual && typeof params.visual.rotSnapDeg === 'number')
      ? params.visual.rotSnapDeg
      : 15;

    if (ev.altKey) {
      step = 0;          // livre
    } else if (ev.ctrlKey) {
      step = 5;          // fino
    } else if (ev.shiftKey) {
      step = 45;         // grosso
    }

    if (step) deltaDeg = Math.round(deltaDeg / step) * step;
    // ==============================================

    if (obs.comboGroup) {
      const comboParts = state.obstacles.filter(o => o.comboGroup === obs.comboGroup)
      comboParts.forEach(p => {
        const orig = startData.find(s => s.id === p.id)
        const rad = deltaDeg * Math.PI / 180
        const rdx = orig.relX * Math.cos(rad) - orig.relY * Math.sin(rad)
        const rdy = orig.relX * Math.sin(rad) + orig.relY * Math.cos(rad)
        p.xm = centerX + rdx
        p.ym = centerY + rdy
        p.rot = (orig.rot + deltaDeg) % 360
      })
    } else {
      obs.rot = (startData[0].rot + deltaDeg) % 360
    }
  }
}

function onPointerUp() { state.drag.active = false }

/* ===================== Medição & Percurso ===================== */

function clearCourse() {
  historyPush('clearCourse');
  state.tools.course = []
}

function addMeasurePoint(xm, ym) {
  if (state.tools.measurePts.length >= 2) state.tools.measurePts = []
  state.tools.measurePts.push({ x: xm, y: ym })
}

function onObstacleClick(obs, event) {
  const isCtrl = event?.ctrlKey || event?.metaKey

  if (state.tools.pathActive) {
    const inMontar = state.tools.clickMode === 'montar'
    const shouldToggle = (isCtrl || inMontar)

    if (shouldToggle) {
      const idx = state.tools.course.findIndex(c => c.id === obs.id)
      if (idx >= 0) {
        state.tools.course.splice(idx, 1)
      } else {
        state.tools.course.push({ id: obs.id })
      }
      return
    }
  }
  state.selectedId = obs.id
}

function courseLabel(obs) {
  if (obs.type === 'start' || obs.type === 'finish') return ''

  const courseFiltered = state.tools.course.filter(c => {
    const o = state.obstacles.find(ob => ob.id === c.id)
    return o && o.type !== 'start' && o.type !== 'finish'
  })

  const comboBaseMap = {}
  let displayNumber = 1

  courseFiltered.forEach(c => {
    const o = state.obstacles.find(ob => ob.id === c.id)
    if (!o) return

    if (o.comboGroup) {
      if (!(o.comboGroup in comboBaseMap)) {
        comboBaseMap[o.comboGroup] = displayNumber++
      }
    } else {
      displayNumber++
    }
  })

  const obsInCourse = courseFiltered.find(c => c.id === obs.id)
  if (!obsInCourse) return ''

  if (obs.comboGroup) {
    const base = comboBaseMap[obs.comboGroup] || 0
    return `${base}${obs.comboPart || ''}`
  }

  let num = 1
  const seenCombos = new Set()

  for (const c of courseFiltered) {
    const o = state.obstacles.find(ob => ob.id === c.id)
    if (!o) continue
    if (o.comboGroup) {
      if (!seenCombos.has(o.comboGroup)) {
        seenCombos.add(o.comboGroup)
        if (o.id === obs.id) return String(num)
        num++
      }
    } else {
      if (o.id === obs.id) return String(num)
      num++;
    }
  }
  return ''
}

const measureP0 = computed(() => state.tools.measurePts[0] || null)
const measureP1 = computed(() => state.tools.measureHover || state.tools.measurePts[1] || null)

const measureDistance = computed(() => {
  const a = measureP0.value, b = measureP1.value
  if (!a || !b) return 0
  const dx = b.x - a.x, dy = b.y - a.y
  return Math.hypot(dx, dy)
})

const measureLine = computed(() => {
  const a = measureP0.value
  if (!a) return null
  const b = measureP1.value || a
  return { x1: a.x, y1: a.y, x2: b.x, y2: b.y }
})

const measureLabelStyle = computed(() => {
  const b = measureP1.value
  if (!b) return { display: 'none' }
  return { left: `${b.x * state.scale + 8}px`, top: `${b.y * state.scale - 22}px` }
})



/* ===================== Utils ===================== */

const vertexDrag = ref({ active:false, idx:-1 })

function onVertexDown(ev, idx){
  ev.currentTarget?.setPointerCapture?.(ev.pointerId)
  vertexDrag.value = { active: true, idx }
  window.addEventListener('pointermove', onVertexMove)
  window.addEventListener('pointerup', onVertexUp, { once:true })
}

function onVertexMove(ev){
  if (!vertexDrag.value.active) return
  // Converte coordenada de tela → arena (m), respeitando pan/zoom
  const { xm, ym } = screenToArena(ev.clientX, ev.clientY)
  const off = arenaGeom.value.offset
  let x = xm + off.x
  let y = ym + off.y
  // Snap opcional
  if (state.snap){
    const gridStep = params.arena.snapGrid || 0.5
    x = Math.round(x / gridStep) * gridStep
    y = Math.round(y / gridStep) * gridStep
  }
  // Atualiza o vértice em METROS
  state.free.verts[vertexDrag.value.idx] = { x, y }
}

function onVertexUp(){
  vertexDrag.value.active = false
  window.removeEventListener('pointermove', onVertexMove)
}

// Tipos de peça única (sem spread) onde faz sentido derivar heights a partir de hm
const SINGLE_BAR_TYPES = ['vertical','gate','plank','wall']
function normalizeObstacleHeightsOnBoot(p){
  if (!p?.obstacles) return
  for (const [t,base] of Object.entries(p.obstacles)){
    if (Array.isArray(base?.heights) && base.heights.length) continue
    if (SINGLE_BAR_TYPES.includes(t) && Number.isFinite(+base?.hm)){
      p.obstacles[t] = { ...base, heights:[Number(base.hm)] }
    }
  }
}


// === MEDIÇÃO DINÂMICA ===
// ponto “flutuante” enquanto move o mouse após o 1º clique
if (!('measureHover' in state.tools)) state.tools.measureHover = null

function pointerToArenaMeters(ev) {
  const res = screenToArena(ev.clientX, ev.clientY)
  if (!res || res.ok !== true) return null
  const off = (arenaGeom.value?.offset) || { x: 0, y: 0 }
  return { x: res.xm + off.x, y: res.ym + off.y }
}

function onMeasureMove(ev) {
  if (!state.tools.measureActive) return
  if (state.tools.measurePts.length !== 1) return
  const p = pointerToArenaMeters(ev)
  if (!p) return                    // sem elemento válido: ignore o move
  state.tools.measureHover = p
}

// se desativar a medição, limpa e remove o listener
watch(() => state.tools.measureActive, (on) => {
  if (!on) {
    state.tools.measurePts = []
    state.tools.measureHover = null
    window.removeEventListener('pointermove', onMeasureMove)
  }
})

// function clamp(v,a,b){ return Math.max(a, Math.min(b, v)) } // está sendo importada

// PIP (ray casting)
function pointInPoly(p, poly){
  let inside = false
  for (let i=0, j=poly.length-1; i<poly.length; j=i++){
    const pi = poly[i], pj = poly[j]
    const inter = ((pi.y>p.y)!=(pj.y>p.y)) &&
      (p.x < (pj.x-pi.x)*(p.y-pi.y)/((pj.y-pi.y)||1e-12) + pi.x)
    if (inter) inside = !inside
  }
  return inside
}

function bboxOf(points){
  let minX=Infinity,minY=Infinity,maxX=-Infinity,maxY=-Infinity
  for (const p of points){ if (p.x<minX)minX=p.x; if (p.y<minY)minY=p.y; if (p.x>maxX)maxX=p.x; if (p.y>maxY)maxY=p.y }
  return { minX, minY, maxX, maxY }
}

// Limita (dx,dy) de um GRUPO para caber no retângulo atual da arena
function confineDeltaGroupRect(startPts, dx, dy, W, H){
  const bb = bboxOf(startPts)
  const minDx = - (bb.minX - 0)
  const maxDx =   (W - bb.maxX)
  const minDy = - (bb.minY - 0)
  const maxDy =   (H - bb.maxY)
  return { dx: clamp(dx, minDx, maxDx), dy: clamp(dy, minDy, maxDy) }
}

// Limita (dx,dy) de um GRUPO ao polígono (convexo de 4 lados) via binary search em t∈[0,1]
function confineDeltaGroupPoly(startPts, dx, dy, poly){
  // se já cabe inteiro, retorna 1
  const allInside = (t) => startPts.every(p => pointInPoly({ x: p.x + dx*t, y: p.y + dy*t }, poly))
  if (allInside(1)) return { dx, dy }
  // busca o maior t que mantém todos dentro
  let lo = 0, hi = 1
  for (let i=0;i<22;i++){ // ~1e-6 de precisão
    const mid = (lo+hi)/2
    if (allInside(mid)) lo = mid; else hi = mid
  }
  return { dx: dx*lo, dy: dy*lo }
}

// function closestOnSeg(ax,ay,bx,by, px,py){
//   const abx=bx-ax, aby=by-ay
//   const apx=px-ax, apy=py-ay
//   const denom = (abx*abx+aby*aby) || 1e-12
//   let t = (apx*abx + apy*aby) / denom
//   t = t<0?0:(t>1?1:t)
//   return { x: ax + abx*t, y: ay + aby*t }
// }

// Confina ao retângulo (rect) ou ao polígono (4 lados)
// function confineToArena(x, y){
//   // retangular
//   if (state.arenaMode === 'rect' || !state.free?.confine){
//     return { x: clamp(x, 0, state.arenaWm), y: clamp(y, 0, state.arenaHm) }
//   }
//   const poly = arenaGeom.value?.poly
//   if (!poly || poly.length < 3) return { x, y }

//   const p = { x, y }
//   if (pointInPoly(p, poly)) return p

//   // fora: projeta no segmento mais próximo
//   let best = null, bestD2 = Infinity
//   for (let i=0, j=poly.length-1; i<poly.length; j=i++){
//     const a = poly[j], b = poly[i]
//     const q = closestOnSeg(a.x, a.y, b.x, b.y, x, y)
//     const dx = q.x - x, dy = q.y - y
//     const d2 = dx*dx + dy*dy
//     if (d2 < bestD2){ bestD2 = d2; best = q }
//   }
//   return best || p
// }



/* ===================== Funções para o PDF ===================== */

// =============== Formatação =================
function toBR(n, digs = 2) { return (n==null||Number.isNaN(+n)) ? '—' : (+n).toFixed(digs).replace('.', ',') }
function fmtM(n, digs = 2) { return n==null ? '—' : `${toBR(n, digs)} m` }
function fmtDeg(n, digs = 1) { return `${toBR(n, digs)}°` }
function angleVsBaseC(rotDeg){ let a=((rotDeg%180)+180)%180; return a>90?180-a:a }

// "A 24,50 | B 52,00 | C 5,50 | D 8,00"
function centroStrM(d){ return `A ${toBR(d.A)} | B ${toBR(d.B)} | C ${toBR(d.C)} | D ${toBR(d.D)}` }

// cálculo das distâncias pro retângulo
function distABCD(x, y, arenaWm, arenaHm){ return {A:y, B:Math.max(0, arenaWm-x), C:Math.max(0, arenaHm-y), D:x} }

// Centros das barras ao longo da normal de frente
function barCentersM(obs) {
  const spec = getObsSpecs(obs) // já tens
  const th = (spec.thicknesses?.length ? spec.thicknesses : [spec.barThickness ?? 0.10])
  const gp = spec.gaps || []
  const n  = frontNormal(obs) // já tens
  const total = th.reduce((a,b)=>a+b,0) + gp.reduce((a,b)=>a+b,0)

  const centers = []
  let offset = -total/2 + th[0]/2
  for (let i=0; i<th.length; i++) {
    centers.push({ x: obs.xm + n.x * offset, y: obs.ym + n.y * offset })
    offset += th[i] + (i < gp.length ? gp[i] : 0)
  }
  return centers
}

// Número do obstáculo para a tabela
function obstacleNumber(obs) {
  const n = (typeof courseLabel === 'function' ? courseLabel(obs) : '') // tua função
  return n || obs.label || String(obs.id)
}

function normalizeSpecs(spec, fallbackThickness = 0.10) {
  const th = (spec.thicknesses?.length ? spec.thicknesses
            : (spec.barThickness != null ? [spec.barThickness] : []))
  const gaps = spec.gaps || []
  return {
    wm: spec.wm,
    hm: spec.hm,                // <— ADICIONE ISTO
    heights: spec.heights || [],
    thicknesses: th.length ? th : [fallbackThickness],
    gaps
  }
}

function parseLabelNumSuf(str){
  const s = String(str || '').trim().toLowerCase()
  const m = s.match(/^(\d+)([a-z])?$/i)
  if (!m) return { n: Number.isFinite(+s) ? +s : 9999, suf: '' }
  return { n: parseInt(m[1], 10), suf: (m[2] || '') }
}

const orderedObstacles = computed(() => {
  const arr = (Array.isArray(state.obstacles) ? state.obstacles.slice() : []).filter(Boolean)
  return arr.sort((a, b) => {
    const A = orderKeyForObstacle(a)
    const B = orderKeyForObstacle(b)
    return (A.grp - B.grp) || (A.n - B.n) || A.suf.localeCompare(B.suf)
  })
})


function orderKeyForObstacle(o) {
  if (o.type === 'start')  return { grp: 0, n: 0,     suf: ''   } // primeiro
  if (o.type === 'finish') return { grp: 2, n: 999999, suf: 'zz' } // último
  const label = obstacleNumber(o)
  const { n, suf } = parseLabelNumSuf(label)
  return { grp: 1, n, suf } // meio (ordem natural)
}


// Monta as linhas da tabela por tipo
function buildObstacleRowsForPDF() {
  const rows = []
  for (const o of orderedObstacles.value) {
    const spec = specFor(o) // ok usar; as funcs internas também chamam getObsSpecs
    const t = String(o?.type || '').toLowerCase()

    if (t === 'start') {
      rows.push(rowStartFinish(o, 'Start'))
      continue
    }
    if (t === 'finish') {
      rows.push(rowStartFinish(o, 'Finish'))
      continue
    }
    if (t === 'gate') {
      rows.push(rowGateLike(o, 'Gate'))
      continue
    }
    if (t === 'plank') {
      rows.push(rowGateLike(o, 'Plank'))
      continue
    }
    if (t === 'muro' || t === 'wall') {
      rows.push(rowGateLike(o, 'Muro'))
      continue
    }
    if (t === 'cross' || t === 'x') {
      // no original, o Cross mostrava como vertical (mudando só o label)
      const r = rowVertical(o); r.tipo = 'Cross-rail'; rows.push(r)
      continue
    }
    if (t === 'oxer') {
      rows.push(rowOxer(o, spec)); continue
    }
    if (t === 'triple' || t === 'triplebar') {
      rows.push(rowTripleBar(o, spec)); continue
    }
    // fallback
    rows.push(rowVertical(o, spec))
  }
  return rows
}


function drawArenaFacesABCD(pdf, x, y, w, h) {
  // x,y = canto superior esquerdo da imagem do mapa | w,h = tamanho da imagem no PDF (mm)
  pdf.setFontSize(11)
  pdf.setFont(undefined, 'bold')

  const off = 3.2 // afastamento em mm da borda da imagem

  // A (topo)
  pdf.text('A', x + w / 2, y - off, { align: 'center' })
  // B (direita)
  pdf.text('B', x + w + off, y + h / 2, { align: 'left' })
  // C (base)
  pdf.text('C', x + w / 2, y + h + off, { align: 'center' })
  // D (esquerda)
  pdf.text('D', x - off, y + h / 2, { align: 'right' })
}


// Captura “limpa” do mapa e gera PDF
async function gerarRelatorioMontagemPDF() {
  // esconder handles/medidas e normalizar view
  const prevHandles = state.free.showHandles
  const prevMeasure = state.tools.measureActive
  const prevView    = { ...state.view }
  state.free.showHandles = false
  state.tools.measureActive = false
  state.view.scale = 1; state.view.x = 0; state.view.y = 0
  await nextTick()

  try {
    const canvas = await html2canvas(arenaRef.value, { backgroundColor:'#FFF', scale:2, useCORS:true })
    const img    = canvas.toDataURL('image/png')

    // >>> Paisagem para caber tabela
    const pdf = new jsPDF('l', 'mm', 'a4')
    const pageW = pdf.internal.pageSize.getWidth()
    const pageH = pdf.internal.pageSize.getHeight()
    const margin = 10

    // PÁGINA 1 — CAPA COM MAPA
    pdf.setFontSize(13)
    pdf.text('Relatório de Montagem – Mapa Técnico', pageW/2, margin - 2, { align:'center' })
    pdf.setFontSize(9)
    pdf.text(
      `Arena: ${toBR(state.arenaWm)} × ${toBR(state.arenaHm)} m  ·  Escala: ${Math.round(state.scale)} px/m`,
      margin,
      margin + 3
    )

    // --- NOVO BLOCO: tempos do percurso ---
    pdf.setFontSize(9)
    pdf.text(
      `Comprimento do percurso: ${toBR(courseMeters.value)} m  -  TA: ${fmtSec(taSec.value)} | TL: ${fmtSec(tlSec.value)}  -  Tempo Ótimo (Tabela IV): ${fmtSec(optSec.value)}`,
      margin,
      margin + 8
)
    // --------------------------------------

    // mapa ocupando bem a área
    const maxW = pageW - margin*2
    const maxH = pageH - margin*2 - 8
    const ratio = canvas.width / canvas.height
    let imgW = maxW, imgH = imgW/ratio
    if (imgH > maxH) { imgH = maxH; imgW = imgH * ratio }

    const imgX = (pageW - imgW) / 2
    const imgY = margin + 18   // empurra um pouco pra baixo, pois agora temos cabeçalho extra

    pdf.addImage(img, 'PNG', imgX, imgY, imgW, imgH)

    // >>> Desenhar A/B/C/D nas faces
    drawArenaFacesABCD(pdf, imgX, imgY, imgW, imgH)

    // PÁGINA 2+ — TABELA
    pdf.addPage()
    const rows = buildObstacleRowsForPDF() // usa o novo centroStrM e extras curtos
    renderTabelaObstaculos(pdf, rows, { margin })

    pdf.setFontSize(8)
    pdf.text(
      'Âng. C = ângulo em relação ao lado C (base). Centro A/B/C/D em metros. gap = face-a-face, c2c = centro-a-centro.',
      10,
      pdf.internal.pageSize.getHeight() - 8
    )

    pdf.save(`relatorio-pista-${new Date().toISOString().slice(0,10)}.pdf`)
  } finally {
    state.free.showHandles = prevHandles
    state.tools.measureActive = prevMeasure
    state.view = prevView
  }
}


function rowVertical(o) {
  const spec = normalizeSpecs(getObsSpecs(o))
  const ang  = angleVsBaseC(o.rot)
  const d    = distABCD(o.xm, o.ym, state.arenaWm, state.arenaHm)
  // fallback: hm quando não houver altura de barra informada
  const hTop = heightsForBars(o, 1, [spec.hm ?? params.obstacles?.vertical?.hm])[0]
  return {
    num: obstacleNumber(o),
    tipo: 'Vertical',
    largura: fmtM(spec.wm),
    angulo: fmtDeg(ang),
    centroABCD: centroStrM(d),
    extras: `Altura: ${fmtM(hTop)}`
  }
}

function resolveThicknesses(o){
  const spec = (getObsSpecs?.(o)) || {}
  if (Array.isArray(spec.thicknesses) && spec.thicknesses.length) {
    return spec.thicknesses.map(n => Number(n)).filter(Number.isFinite)
  }
  const bt = Number(spec.barThickness)
  const t  = Number.isFinite(bt) ? bt : 0.10
  return [t, t, t] // serve para oxer/triple; usaremos os que precisarmos
}


function resolveGaps(o){ // local > objeto > preset
  const spec  = (getObsSpecs?.(o)) || {}
  const local = o?.local || {}
  const arr = pickArray(local.gaps, o.gaps, spec.gaps)
  return arr ? arr.map(Number).filter(Number.isFinite) : []
}


// (opcional) padronizar alturas no Oxer/Triple também:
function rowOxer(o) {
  const spec    = normalizeSpecs(getObsSpecs(o)) // usa wm do preset
  const ang     = angleVsBaseC(o.rot)
  const centers = barCentersM(o)
  const p1      = centers[0] || { x: o.xm, y: o.ym }
  const d1      = distABCD(p1.x, p1.y, state.arenaWm, state.arenaHm)

  // ESTRITO: lê do objeto/local/preset (sem chutar)
  const th  = resolveThicknesses(o)       // [t0, t1, …]
  const gp  = resolveGaps(o)              // [g0, …]
  const H   = resolveHeights(o)           // [h0, h1]

  // c2c[i] = gap[i] + (th[i] + th[i+1]) / 2
  const c2cList = gp.map((g, i) => g + (th[i] + (th[i + 1] ?? th[i])) / 2)

  const extrasParts = []
  if (gp.length) {
    extrasParts.push(
      `gap: ${gp.map(v => toBR(v, 2)).join(' | ')} | c2c: ${c2cList.map(v => toBR(v, 2)).join(' | ')}`
    )
  }
  // Alturas sempre mostradas (se não houver, sai "—")
  extrasParts.push(`Alturas: ${[0, 1].map(i => fmtM(H[i] ?? null)).join(' / ')}`)

  return {
    num: obstacleNumber(o),
    tipo: 'Oxer',
    largura: fmtM(spec.wm),
    angulo: fmtDeg(ang),
    centroABCD: `1ª barra – ${centroStrM(d1)}`,
    extras: extrasParts.join('  |  ')
  }
}

function rowTripleBar(o) {
  const spec    = normalizeSpecs(getObsSpecs(o)) // usa wm do preset
  const ang     = angleVsBaseC(o.rot)
  const centers = barCentersM(o)
  const p1      = centers[0] || { x: o.xm, y: o.ym }
  const d1      = distABCD(p1.x, p1.y, state.arenaWm, state.arenaHm)

  // ESTRITO: lê do objeto/local/preset (sem chutar)
  const th  = resolveThicknesses(o)       // [t0, t1, t2]
  const gp  = resolveGaps(o)              // [g0, g1]
  const H   = resolveHeights(o)           // [h0, h1, h2]

  const c2cList = gp.map((g, i) => g + (th[i] + (th[i + 1] ?? th[i])) / 2)

  const extrasParts = []
  if (gp.length) {
    extrasParts.push(
      `gap: ${gp.map(v => toBR(v, 2)).join(' | ')} | c2c: ${c2cList.map(v => toBR(v, 2)).join(' | ')}`
    )
  }
  // Alturas sempre mostradas (se não houver, sai "—")
  extrasParts.push(`Alturas: ${[0, 1, 2].map(i => fmtM(H[i] ?? null)).join(' / ')}`)

  return {
    num: obstacleNumber(o),
    tipo: 'Triple bar',
    largura: fmtM(spec.wm),
    angulo: fmtDeg(ang),
    centroABCD: `1ª barra – ${centroStrM(d1)}`,
    extras: extrasParts.join('  |  ')
  }
}

function rowGateLike(o, label) {
  const spec = normalizeSpecs(getObsSpecs(o))
  const ang  = angleVsBaseC(o.rot)
  const d    = distABCD(o.xm, o.ym, state.arenaWm, state.arenaHm)
  const hTop = heightsForBars(o, 1, [spec.hm])[0]
  const t    = spec.thicknesses?.[0]
  const ex   = [
    `Altura: ${fmtM(hTop)}`,
    (t != null ? `Esp.: ${fmtM(t)}` : '')
  ].filter(Boolean).join(' | ')
  return {
    num: obstacleNumber(o),
    tipo: label,
    largura: fmtM(spec.wm),
    angulo: fmtDeg(ang),
    centroABCD: centroStrM(d),
    extras: ex
  }
}


function rowStartFinish(o, label) {
  const ang  = angleVsBaseC(o.rot)
  const d    = distABCD(o.xm, o.ym, state.arenaWm, state.arenaHm)
  const spec = normalizeSpecs(getObsSpecs(o))
  return {
    num: '',
    tipo: label,
    largura: fmtM(spec.wm),
    angulo: fmtDeg(ang),
    centroABCD: centroStrM(d),
    extras: ''
  }
}

function renderTabelaObstaculos(pdf, rows, opts = {}) {
  const margin   = opts.margin ?? 10
  const pageW    = pdf.internal.pageSize.getWidth()
  const pageH    = pdf.internal.pageSize.getHeight()
  const contentW = pageW - margin*2
  const lineH    = 5
  const padX     = 1.2

  // colunas mais curtas no começo, espaço amplo p/ Centro e Extras
  const cols = [
    { key:'num',       head:'#',         w: 10 },
    { key:'tipo',      head:'Tipo',      w: 22 },
    { key:'largura',   head:'Larg.',     w: 18 },
    { key:'angulo',    head:'Âng. C',    w: 18 },
    { key:'centroABCD',head:'Centro A/B/C/D (m)', w: 70 },
  ]
  cols.push({ key:'extras', head:'Extras', w: contentW - cols.reduce((s,c)=>s+c.w,0) })

  let y = margin
  pdf.setFontSize(9)

  const drawHeader = () => {
    let x = margin
    pdf.setFont(undefined, 'bold')
    cols.forEach(c => { pdf.text(c.head, x+padX, y); x += c.w })
    pdf.setFont(undefined, 'normal')
    y += 2
    pdf.setLineWidth(0.2); pdf.line(margin, y, pageW - margin, y)
    y += 3
  }

  const ensureSpace = (needH) => {
    if (y + needH > pageH - margin) {
      pdf.addPage(); y = margin; drawHeader()
    }
  }

  drawHeader()
  pdf.setFontSize(8.5)

  for (const r of rows) {
    // prepara quebra de linha por coluna
    const wrapped = cols.map(c => {
      const txt = String(r[c.key] ?? '')
      return pdf.splitTextToSize(txt, c.w - padX*2)
    })
    const rowH = Math.max(...wrapped.map(w => w.length)) * lineH

    ensureSpace(rowH)

    // desenha célula a célula
    let x = margin
    for (let i=0;i<cols.length;i++){
      const lines = wrapped[i]
      // imprimir linha a linha
      for (let k=0;k<lines.length;k++){
        pdf.text(lines[k], x+padX, y + lineH*(k+0.8))
      }
      x += cols[i].w
    }
    y += rowH
  }

  // rodapé (paginador)
  const pageCount = pdf.getNumberOfPages?.() ?? 1
  for (let i=1;i<=pageCount;i++){
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.text(`Página ${i}/${pageCount}`, pageW - margin, pageH - 4, { align:'right' })
  }
}

function pickArray(...cands){
  for (const c of cands) if (Array.isArray(c) && c.length) return c
  return null
}
function resolveHeights(o){
  const spec  = (getObsSpecs?.(o)) || params?.obstacles?.[o.type] || {}
  const local = o?.local || {}
  const arr =
    (Array.isArray(local.heights)     && local.heights.length)     ? local.heights :
    (Array.isArray(local.barHeights)  && local.barHeights.length)  ? local.barHeights :
    (Array.isArray(o.heights)         && o.heights.length)         ? o.heights :
    (Array.isArray(o.barHeights)      && o.barHeights.length)      ? o.barHeights :
    (Array.isArray(spec.heights)      && spec.heights.length)      ? spec.heights :
    (Array.isArray(spec.barHeights)   && spec.barHeights.length)   ? spec.barHeights :
    [];
  return arr.map(Number).filter(Number.isFinite);
}

function heightsForBars(o, count, fallbacks = []) {
  const H = resolveHeights(o)
  const out = []
  for (let i=0;i<count;i++){
    const cand = H[i]
    out.push(Number.isFinite(cand) ? cand
           : (Number.isFinite(fallbacks[i]) ? fallbacks[i] : null))
  }
  return out
}

// ----------------- FIM DO PDF -------------------


watch([() => state.arenaWm, () => state.arenaHm, () => state.arenaMode],
([w,h,mode]) => {
  if (mode !== 'rect') {
    state.free.verts = [
      { x: 0, y: 0 }, { x: w, y: 0 },
      { x: w, y: h }, { x: 0, y: h }
    ]
  }
}, { immediate: true })

watch(() => state.arenaMode, async (m) => {
  if (m === 'rect') {
    await nextTick()
    state.free.showHandles = true
  }
})

// Guarda a última posição da ENTRADA
const lastEntry = ref(null)

watch(selected, (o) => {
  if (!o) { lastEntry.value = null; return }
  lastEntry.value = entryBarCenter(o, getObsSpecs(o))
}, { immediate: true })

watch(selected, (o) => {
  if (!o?.comboGroup) return

  const groupId = o.comboGroup
  const parts = state.obstacles.filter(p => p.comboGroup === groupId)
  const gapsCount = Math.max(0, parts.length - 1)

  const stored = getComboFaces(groupId, gapsCount)
  const geom   = deriveComboFacesFromGeometry(groupId, o)

  const tooDifferent =
    stored.length !== geom.length ||
    stored.some((v, i) => Math.abs((+v || 0) - (geom[i] || 0)) > 0.02)

  if (tooDifferent) setComboFaces(groupId, geom)
}, { immediate: false })


watch(
  () => {
    const o = selected.value
    if (!o) return null
    const s = getObsSpecs(o)
    return {
      id:  o.id,
      rot: o.rot,
      dir: s.dir,
      thK: (s.thicknesses || []).join(';'),
      gpK: (s.gaps || []).join(';'),
    }
  },
  (cur, prev) => {
    const o = selected.value
    if (!o || !cur || !prev) return
    const changed = cur.thK !== prev.thK || cur.gpK !== prev.gpK || cur.dir !== prev.dir
    if (!changed) return

    // 1) ancora pela barra de ENTRADA (mantém a “primeira barra” no lugar)
    const was = lastEntry.value
    const now = entryBarCenter(o, getObsSpecs(o))
    if (was) {
      const dx = was.x - now.x
      const dy = was.y - now.y
      if (Math.abs(dx) > 1e-6 || Math.abs(dy) > 1e-6) {
        o.xm += dx
        o.ym += dy
      }
    }

    // 2) reflow do combo para preservar a distância face-a-face entre adjacentes
    reflowComboFromChanged(o)

    // 3) atualiza a referência da entrada
    lastEntry.value = entryBarCenter(o, getObsSpecs(o))
  },
  { deep: false }
)



/* ===================== Eventos globais ===================== */

let offHotkeys

onMounted(() => {
  try { const stored = loadParamsFromLocal(); if (stored) { mergeParams(stored) } } catch (e) { console.warn('[Montador] load params failed:', e) }


  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
  window.addEventListener('keydown', onKey)
  const isEditableEl = (el) => {
    const tag = (el?.tagName || '').toLowerCase()
    return el?.isContentEditable || ['input','textarea','select'].includes(tag)
  }
  window.addEventListener('keydown', (e) => {
    if (paramsModal.open || isEditableEl(e.target)) return
    if (e.code === 'Space') spacePressed = true
  })
  window.addEventListener('keyup', (e) => {
    if (paramsModal.open || isEditableEl(e.target)) return
    if (e.code === 'Space') spacePressed = false
  })
  historyPush('init')
  offHotkeys = registerHistoryHotkeys(window, history)

  // Prefere 375 m/min se área >= 5.000 m²; caso contrário 350 m/min
  const area = Number(state.arenaWm || 0) * Number(state.arenaHm || 0);
  speedPreset.value = area >= 5000 ? 375 : 350;
  normalizeObstacleHeightsOnBoot(params);

})
onBeforeUnmount(() => {
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  window.removeEventListener('keydown', onKey)
  offHotkeys?.()
})

</script>

<style scoped>
/* Layout (apenas o que é do PAI) */
.builder-wrap {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 16px;
  height: 100%;
}
.sidebar {
  background: #0b1020;
  color: #e8eefc;
  padding: 16px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.25);
  max-height: calc(100vh - 32px);
  overflow: auto;
}
.title { margin: 0 0 8px; font-size: 20px; }

/* Arena (container do pai) */
.arena {
  position: relative;
  background: #eaeef3;
  border-radius: 16px;
  box-shadow: inset 0 0 0 2px #cfd5dc, 0 6px 24px rgba(0,0,0,.12);
  overflow: visible;
  user-select: none;
}
.content {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 16px;
}
.grid {
  position: absolute; inset: 0;
  background-image:
    linear-gradient(to right, rgba(0,0,0,.08) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(0,0,0,.08) 1px, transparent 1px);
  pointer-events: none;
}

/* Medição (overlay do pai) */
.measure { position: absolute; inset: 0; pointer-events: none; }
.measure line { stroke: #2563eb; stroke-width: 2; stroke-dasharray: 6 4; }
.measure-label {
  position: absolute;
  padding: 2px 6px;
  background: rgba(0,0,0,.45);
  color: #fff;
  font-weight: 700;
  border-radius: 8px;
  font-size: 12px;
  pointer-events: none;
}
.mode-badge {
  position: absolute; left: 10px; top: 10px;
  background: rgba(0,0,0,.5); color: #cfe2ff;
  padding: 4px 8px; border-radius: 8px; font-size: 12px; font-weight: 700;
}

/* Linhas do percurso (ficam sob os obstáculos/arena) */
.course-lines { position: absolute; inset: 0; pointer-events: none; }

/* Linha de medição — mais fina */
.measure .measure-line {
  stroke: #10b981;      /* pode trocar a cor */
  stroke-width: 1.5;    /* antes 3 */
  stroke-linecap: round;
  stroke-dasharray: 6 4;  /* antes 8 6 */
  opacity: 0.60;

}

/* Enquanto estiver seguindo o mouse (preview), tracejado mais “miúdo” */
.measure .measure-line.preview {
  stroke-dasharray: 6 4;  /* antes 8 6 */
  opacity: 0.60;
  filter: drop-shadow(0 0 2px rgba(16,185,129,.45));
}

/* Pontos das extremidades */
.measure .measure-point {
  fill: #10b981;
  stroke: #fff;
  stroke-width: 1;
}
.measure .measure-point.end.preview {
  /* quando está seguindo o mouse, destaque o ponto que “anda” */
  fill: #34d399;
}

</style>

<style>
/* ===== Base da sidebar — isolada pela namespace .montador ===== */
.montador .section { border-top:1px dashed #334; padding-top:12px; margin-top:12px; }
.montador .label { font-size:12px; opacity:.85; display:block; margin-bottom:6px; }
.montador .row { display:grid; grid-template-columns:1fr 1fr; gap:8px; align-items:center; margin-bottom:8px; }
.montador .row.actions { grid-template-columns:1fr 1fr 1fr; }
.montador .btn { background:#1a2a4a; color:#e8eefc; border:1px solid #2a3a5a; border-radius:10px; padding:8px 10px; cursor:pointer; }
.montador .btn:hover { background:#22365e; }
.montador .btn.active { box-shadow:0 0 0 2px #8ab6ff inset; }
.montador .checkbox { display:flex; align-items:center; gap:8px; margin-top:8px; }
.montador .hint { font-size:12px; opacity:.7; margin-top:4px; }

/* Subtítulos/“cards” dentro da sidebar */
.montador .section-subtitle {
  margin-bottom: 6px; font-size: 13px; font-weight: bold;
  color: #9fc5ff; text-transform: uppercase; letter-spacing: .5px;
}
.montador .section-individual {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px; margin-top: 10px;
  background: rgba(255, 255, 255, 0.03);
  box-shadow: inset 0 0 8px rgba(0, 0, 0, 0.3);
}
.montador .section-individual .hint { font-size: 11px; color: #bbb; margin-bottom: 6px; }
.montador .section-individual input[type="range"],
.montador .section-individual input[type="number"],
.montador .section-individual input[type="text"],
.montador .section-individual select {
  background-color: #0f172a; border: 1px solid #334155;
  border-radius: 6px; padding: 4px 6px; color: #fff;
}

/* Esconder marcas quando não está editando */
.montador.builder-wrap:not(.editing) .handle,
.montador.builder-wrap:not(.editing) .entry-mark,
.montador.builder-wrap:not(.editing) .exit-mark {
  display: none !important;
}


</style>